'use client';

import supabase from "@/lib/supabase";
import Link from "next/link";
import { BookOpen, Brain, ChevronDown, ChevronLeft, ChevronRight, FlaskConical, ImagePlus, Pencil, Play, Star, Trophy } from "lucide-react";
import { FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa6";
import Image from "@/components/Image";
import { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from "react";
import { motion } from 'motion/react';
import type { StoredProblem, BoardEntry } from '@/components/whiteboard-v2/types';
import type { DemoSelection } from '@/components/whiteboard-v2/LiveWhiteboard';
import MarkdownComponent from '@/components/Markdown/MarkdownComponent';
import report, { ReportEvent } from "@/lib/report";
import NormalButton from "@/components/Button/normal";
import dynamic from 'next/dynamic';
import { useDelayedLoadForLanding } from "@/hooks/useDelayedLoad";
import useAuthStore from "@/store/useAuthStore";
// Client-side constants (duplicated from page.tsx)
const landing_width = 1440;
const landing_height = 810;
const LANDING_GUEST_LIVE_TUTOR_USED_KEY = 'landing_guest_live_tutor_used';

const platformList = [
  { index: 0, color: '#42C52D' },
  { index: 1, color: '#FBAF2C' },
  { index: 2, color: '#4281E2' }
];

const realVoiceList = [
  {
    name: "Anika",
    content: "7th Grader at a California Public School",
    avatar: "/assets/newlanding_user_5_avatar.png",
    photo: "/assets/newlanding_user_4_photo.png",
    message: `
      It's still in progress but so cool! Suuuuuuper cool. I even showed Pengi to my bro because it's great, and he thinks it's awesome too. I was just in the middle of the pengi course minutes ago!
    `
  },
  {
    name: "Katie",
    content: "6th Grader at a California Private School",
    avatar: "/assets/newlanding_user_11_avatar.png",
    photo: "/assets/newlanding_user_5_photo.png",
    message: `
      What is this Pengi thing? It's like rlly cool, I get to learn everything from my school beforehand — THIS IS A MIRACLE. IT HAS THE EXACT BOOKS TOO, MY LIFE HAS BECOME TOO GOOD TO BE TRUE! I use it like every day now, even on weekends. It doesn't even feel like studying tbh.
    `
  },
  {
    name: "Mr. Anson",
    content: "Parent from a DC Public School",
    avatar: "/assets/newlanding_user_6_avatar.png",
    photo: "/assets/newlanding_user_6_photo.png",
    message: `
      As a DC public school parent, I've always struggled to find better learning resources. I mentioned wanting a Diagnostic Test to track my child's math progress, and the team built it right away and invited me to try it. Their quick action shows they're truly trustworthy and committed to helping kids learn.
    `
  },
  {
    name: "Ms. Ramirez",
    content: "Math Tutor Specializing in AMC Competition",
    avatar: "/assets/newlanding_user_1_avatar.png",
    photo: "/assets/newlanding_user_1_photo3.png",
    message: "The way Pengi breaks down AMC problems is exactly how I teach. My students get real practice at home, which is hard to find anywhere else."
  },
  {
    name: "Richard",
    content: "Parent of 4th Grader",
    avatar: "/assets/newlanding_user_2_avatar.png",
    photo: "/assets/newlanding_user_2_photo.png",
    message: `
      Honestly I didn't think he'd stick with it. But he's been on it every day after school. He even got mad when I told him to stop and come eat dinner.
    `
  },
  {
    name: "Avery",
    content: "6th Grader at a California Private School",
    avatar: "/assets/newlanding_user_3_avatar.png",
    photo: "/assets/newlanding_user_3_photo2.png",
    message: `
      Math used to stress me out. Now I feel like I actually know what I'm doing. And the gems are addicting lol.
    `
  },
];

// Redesign C: Katie featured, Mr. Anson removed
const redesignCFeatured = realVoiceList[1]; // Katie
const redesignCGrid = realVoiceList.filter((_, i) => i !== 1 && i !== 2); // exclude Katie & Mr. Anson

const school_photo = [
  '/school_b_1.png',
  '/school_b_7.png',
  '/school_b_8.png',
  '/school_b_6.png',
  '/school_b_5.png',
];

const footerGroups = [
  {
    title: 'Learn',
    links: [
      { label: 'Textbooks', href: '/textbooks' },
      { label: 'Roadmaps', href: '/roadmaps' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Blog', href: '/blog' },
      { label: 'Subjects', href: '/subjects' },
      { label: 'Search', href: '/search' },
      { label: 'About', href: '/about' },
    ],
  },
];

// Lazy load components that are controlled by state
const LoginModal = lazy(() => import("@/components/LoginModal"));
// Lazy load heavy components
const RiveMachine = dynamic(() => import("@/components/RiveComponent/machine"), {
  ssr: false,
  loading: () => <div className="w-full h-full bg-gray-100 rounded-lg animate-pulse" />
});
const Rive = dynamic(() => import("@rive-app/react-canvas"), {
  ssr: false,
  loading: () => <div className="w-full h-full bg-gray-100 rounded-lg animate-pulse" />
});
const ReactPlayer = dynamic(() => import("react-player/lazy"), {
  ssr: false,
  loading: () => <div className="w-full h-full bg-gray-200 rounded-2xl animate-pulse" />
});
const LiveWhiteboard = dynamic(() => import("@/components/whiteboard-v2/LiveWhiteboard"), {
  ssr: false,
  loading: () => <div className="w-full h-[560px] bg-gray-100 rounded-lg animate-pulse" />
});

// Helper function to interpolate between white and themeColor2
const interpolateColor = (opacity: number) => {
  const r1 = 255, g1 = 255, b1 = 255; // White RGB
  const r2 = 220, g2 = 244, b2 = 254; // themeColor2 RGB (#DCF4FE)

  const r = Math.round(r1 + (r2 - r1) * opacity);
  const g = Math.round(g1 + (g2 - g1) * opacity);
  const b = Math.round(b1 + (b2 - b1) * opacity);

  return `rgb(${r}, ${g}, ${b})`;
};

const PREVIEW_BOARD_ENTRIES: BoardEntry[] = [
  { id: 'preview-2', content: 'Expand Each Factor', renderedContent: 'Expand Each Factor', type: 'subheading', timestamp: 0 },
  { id: 'preview-1a', content: 'Power of a product rule: (ab)^n = a^n * b^n', renderedContent: '$\\text{Power of a product: }(ab)^n = a^n \\cdot b^n$', type: 'concept', timestamp: 0 },
  { id: 'preview-1b', content: 'Power of a power rule: (a^m)^n = a^(mn)', renderedContent: '$\\text{Power of a power: }(a^m)^n = a^{mn}$', type: 'concept', timestamp: 0 },
  { id: 'preview-3', content: '(3x^2)^4 = 3^4 * (x^2)^4 = 81x^8', renderedContent: '$(3x^2)^4 = 3^4 \\cdot (x^2)^4 = 81x^8$', type: 'step', timestamp: 0 },
  { id: 'preview-4', content: '(2x^4)^2 = 2^2 * (x^4)^2 = 4x^8', renderedContent: '$(2x^4)^2 = 2^2 \\cdot (x^4)^2 = 4x^8$', type: 'step', timestamp: 0 },
  { id: 'preview-5', content: 'Multiply', renderedContent: 'Multiply', type: 'subheading', timestamp: 0 },
  { id: 'preview-5b', content: 'Product of powers rule: a^m * a^n = a^(m+n)', renderedContent: '$\\text{Product of powers: }a^m \\cdot a^n = a^{m+n}$', type: 'concept', timestamp: 0 },
  { id: 'preview-6a', content: '81x^8 * 4x^8 = (81*4)(x^8 * x^8) = 324x^{16}', renderedContent: '$81x^8 \\cdot 4x^8 = (81 \\cdot 4)(x^8 \\cdot x^8) = \\boxed{324x^{16}}$', type: 'step', timestamp: 0 },
];

export default function LandingClient() {
  // Delayed loading for below-the-fold content (pages 2-7)
  const belowFoldEnabled = useDelayedLoadForLanding();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [showNavLogo, setShowNavLogo] = useState(false);
  const mainLogoRef = useRef<HTMLDivElement>(null);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  // 通过分享的链接进入系统的
  const [isFromShare, setIsFromShare] = useState(false);
  const [currentPlatformIndex, setCurrentPlatformIndex] = useState(0);
  const [currentVoiceIndex, setCurrentVoiceIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const voiceIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const voiceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const testimonialScrollRef = useRef<HTMLDivElement | null>(null);
  const platformScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const platformIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const platformTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [page2Opacity, setPage2Opacity] = useState(0);

  const [page5Opacity, setPage5Opacity] = useState(0);
  const [page7Opacity, setPage7Opacity] = useState(0);
  const page2Ref = useRef<HTMLDivElement | null>(null);
  const page3Ref = useRef<HTMLDivElement | null>(null);
  const page5Ref = useRef<HTMLDivElement | null>(null);
  const page7Ref = useRef<HTMLDivElement | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoPlayerRef = useRef<any>(null);
  const [shouldLoadRive, setShouldLoadRive] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const schoolPhotoContainerRef = useRef<HTMLDivElement>(null);
  const scrollAnimationRef = useRef<number | null>(null);
  const [isHoveringPhotos, setIsHoveringPhotos] = useState(false);
  const user = useAuthStore((state) => state.user);
  const [hasUsedGuestLiveTutor, setHasUsedGuestLiveTutor] = useState(false);

  // ─── Live Tutor section state ────────────────────────────────
  const [liveTutorProblems, setLiveTutorProblems] = useState<StoredProblem[]>([]);
  const [isLoadingProblems, setIsLoadingProblems] = useState(true);
  const [liveTutorSkills, setLiveTutorSkills] = useState<{ skill_id: string; title: string }[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [amcProblems, setAmcProblems] = useState<StoredProblem[]>([]);
  const [isLoadingAmcProblems, setIsLoadingAmcProblems] = useState(true);
  const [historyProblems, setHistoryProblems] = useState<StoredProblem[]>([]);
  const [isLoadingHistoryProblems, setIsLoadingHistoryProblems] = useState(true);
  const [scienceProblems, setScienceProblems] = useState<StoredProblem[]>([]);
  const [isLoadingScienceProblems, setIsLoadingScienceProblems] = useState(true);
  const [liveTutorActiveTab, setLiveTutorActiveTab] = useState<'problems' | 'skills' | 'amc8' | 'history' | 'science'>('problems');
  const [isTabAutoRotating, setIsTabAutoRotating] = useState(true);
  const [liveTutorSelection, setLiveTutorSelection] = useState<DemoSelection | null>(null);
  const [liveTutorStartSignal, setLiveTutorStartSignal] = useState(0);
  const [homeUploadText, setHomeUploadText] = useState('');
  const [homeUploadFile, setHomeUploadFile] = useState<File | null>(null);
  const [homeUploadFileName, setHomeUploadFileName] = useState<string | null>(null);
  const [isHomeUploadMenuOpen, setIsHomeUploadMenuOpen] = useState(false);
  const [isHomeParseLoading, setIsHomeParseLoading] = useState(false);
  const [homeParseErrorMessage, setHomeParseErrorMessage] = useState<string | null>(null);
  const [isHomeParseErrorOpen, setIsHomeParseErrorOpen] = useState(false);
  const [liveTutorWhiteboardStarted, setLiveTutorWhiteboardStarted] = useState(true);
  const carouselScrollRef = useRef<HTMLDivElement | null>(null);
  const liveTutorSectionRef = useRef<HTMLDivElement | null>(null);
  const homeUploadInputRef = useRef<HTMLInputElement | null>(null);
  const homeUploadMenuRef = useRef<HTMLDivElement | null>(null);
  const carouselDragRef = useRef<{ startX: number; startScroll: number } | null>(null);
  const didCarouselDragRef = useRef(false);
  const autoRotationResumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skillKeyPropertyCache = useRef<Map<string, { id: string; title: string; content: string; examples?: string; explanation?: string; picture?: string; picturePosition?: 'below' | 'right' }>>(new Map());
  const lastCarouselTabRef = useRef<string | null>(null);
  const lastCarouselDataLenRef = useRef(0);
  const liveTutorViewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

  const formatProblemLabel = (question: string | undefined) =>
    (question || '').replace(/^Solve\s*for\s*x\s*[:：]\s*/i, '').trim();

  const liveTutorMathSkills = useMemo(() => {
    if (liveTutorProblems.length === 0 || liveTutorSkills.length === 0) return [];
    if (liveTutorSkills.length >= liveTutorProblems.length) {
      return liveTutorSkills.slice(0, liveTutorProblems.length);
    }
    return Array.from({ length: liveTutorProblems.length }, (_, index) => liveTutorSkills[index % liveTutorSkills.length]);
  }, [liveTutorProblems.length, liveTutorSkills]);

  const cardFontSize = liveTutorViewportWidth < 640 ? '14px' : '16px';
  const cardHeight = liveTutorViewportWidth < 640 ? 86 : 101;

  const isGuestSessionLocked = !user && hasUsedGuestLiveTutor;

  const openLoginForGuestLimit = useCallback(() => {
    setLoginModalVisible(true);
  }, []);

  const markGuestLiveTutorUsed = useCallback(() => {
    if (user) return;
    setHasUsedGuestLiveTutor(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANDING_GUEST_LIVE_TUTOR_USED_KEY, '1');
    }
  }, [user]);

  // ─── Live Tutor fetch functions ──────────────────────────────
  const fetchLiveTutorProblems = useCallback(async () => {
    setIsLoadingProblems(true);
    try {
      const endpoints = [
        'whiteboard/problems.json',
        '/whiteboard/problems.json',
        'napi/whiteboard/problems',
        '/napi/whiteboard/problems',
      ];
      let resolved: StoredProblem[] | null = null;
      let firstSuccess: StoredProblem[] | null = null;
      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, { cache: 'no-store' });
          if (!res.ok) continue;
          const raw = await res.json();
          const parsed = Array.isArray(raw) ? raw : (raw?.problems ?? []);
          const normalized = Array.isArray(parsed) ? (parsed as StoredProblem[]) : [];
          if (firstSuccess === null) firstSuccess = normalized;
          if (normalized.length > 0) { resolved = normalized; break; }
        } catch { /* try next */ }
      }
      setLiveTutorProblems(resolved ?? firstSuccess ?? []);
    } catch (error) {
      console.error('Failed to load problems:', error);
      setLiveTutorProblems([]);
    } finally {
      setIsLoadingProblems(false);
    }
  }, []);

  const fetchLiveTutorSkills = useCallback(async () => {
    setIsLoadingSkills(true);
    try {
      const { data: configData, error: configError } = await supabase
        .from('tutor_agent_config')
        .select('value')
        .eq('key', 'whiteboard_preset_skills')
        .maybeSingle();
      if (configError) throw configError;

      const rawValue = configData?.value;
      const parsedValue = typeof rawValue === 'string'
        ? (() => { try { return JSON.parse(rawValue); } catch { return []; } })()
        : rawValue;

      const presetSkillIds = Array.from(
        new Set(
          (Array.isArray(parsedValue) ? parsedValue : Array.isArray((parsedValue as { skills?: unknown[] } | null)?.skills) ? (parsedValue as { skills: unknown[] }).skills : [])
            .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
            .map((id) => id.trim())
        )
      );
      if (presetSkillIds.length === 0) { setLiveTutorSkills([]); return; }

      const { data, error } = await supabase
        .from('syllabus_skill')
        .select('element_id, title, elements_json')
        .in('element_id', presetSkillIds);
      if (error) throw error;

      const skillsById = new Map(
        (data ?? [])
          .filter((item: { element_id: string; title: string | null; elements_json?: unknown }) => item.elements_json !== null)
          .map((item: { element_id: string; title: string | null }) => ({
            skill_id: item.element_id,
            title: (item.title || '').trim(),
          }))
          .filter((item) => item.title.length > 0 && !/untitled/i.test(item.title))
          .map((item) => [item.skill_id, item] as const)
      );
      const orderedSkills = presetSkillIds.map((id) => skillsById.get(id)).filter((item): item is { skill_id: string; title: string } => Boolean(item));
      setLiveTutorSkills(orderedSkills);
    } catch (error) {
      console.error('Failed to load skills:', error);
      setLiveTutorSkills([]);
    } finally {
      setIsLoadingSkills(false);
    }
  }, []);

  const fetchAmcProblems = useCallback(async () => {
    setIsLoadingAmcProblems(true);
    try {
      const res = await fetch('/napi/competitions/amc8-2026/problems');
      if (!res.ok) throw new Error('Failed to load AMC8 questions');
      const data = await res.json();
      setAmcProblems((data.problems ?? []) as StoredProblem[]);
    } catch (error) {
      console.error('Failed to load AMC8 problems:', error);
      setAmcProblems([]);
    } finally {
      setIsLoadingAmcProblems(false);
    }
  }, []);

  const fetchHistoryProblems = useCallback(async () => {
    setIsLoadingHistoryProblems(true);
    try {
      const res = await fetch('/whiteboard/history-problems.json', { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to load history problems');
      const raw = await res.json();
      const parsed = Array.isArray(raw) ? raw : (raw?.problems ?? []);
      setHistoryProblems(parsed as StoredProblem[]);
    } catch (error) {
      console.error('Failed to load history problems:', error);
      setHistoryProblems([]);
    } finally {
      setIsLoadingHistoryProblems(false);
    }
  }, []);

  const fetchScienceProblems = useCallback(async () => {
    setIsLoadingScienceProblems(true);
    try {
      const res = await fetch('/whiteboard/science-problems.json', { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to load science problems');
      const raw = await res.json();
      const parsed = Array.isArray(raw) ? raw : (raw?.problems ?? []);
      setScienceProblems(parsed as StoredProblem[]);
    } catch (error) {
      console.error('Failed to load science problems:', error);
      setScienceProblems([]);
    } finally {
      setIsLoadingScienceProblems(false);
    }
  }, []);

  // ─── Live Tutor skill helpers ────────────────────────────────
  const extractKeyProperty = useCallback((skillId: string, rawElements: unknown) => {
    const elements = Array.isArray(rawElements) ? rawElements : [];
    const keyProps = elements
      .filter((el: { element_type?: string }) => el.element_type === 'key_property')
      .map((el: { id?: string; key_property?: { title?: string; content?: string; examples?: string; explanation?: string; picture?: string; picture_position?: 'left' | 'bottom' } }, idx: number) => ({
        id: el.id || `kp-${idx}`,
        title: el.key_property?.title || `Key Property ${idx + 1}`,
        content: el.key_property?.content || '',
        examples: el.key_property?.examples,
        explanation: el.key_property?.explanation,
        picture: el.key_property?.picture,
        picturePosition: (el.key_property?.picture_position === 'left' ? 'right' : 'below') as 'below' | 'right',
      }));
    if (keyProps.length === 0) return null;
    const preferred = keyProps.find((kp) => kp.content.trim().length > 0 || kp.title.trim().length > 0);
    const selected = preferred ?? keyProps[0];
    skillKeyPropertyCache.current.set(skillId, selected);
    return selected;
  }, []);

  const fetchSkillKeyProperty = useCallback(async (skillId: string) => {
    const cached = skillKeyPropertyCache.current.get(skillId);
    if (cached) return cached;
    const { data, error } = await supabase.from('syllabus_skill').select('elements_json').eq('element_id', skillId).single();
    if (error || !data) return null;
    return extractKeyProperty(skillId, data.elements_json);
  }, [extractKeyProperty]);

  const prefetchSkillKeyProperties = useCallback(async (skillIds: string[]) => {
    const uniqueIds = Array.from(new Set(skillIds)).filter((id) => !skillKeyPropertyCache.current.has(id)).slice(0, 12);
    if (uniqueIds.length === 0) return;
    const { data, error } = await supabase.from('syllabus_skill').select('element_id, elements_json').in('element_id', uniqueIds);
    if (error || !data) return;
    for (const row of data as Array<{ element_id: string; elements_json?: unknown }>) {
      extractKeyProperty(row.element_id, row.elements_json);
    }
  }, [extractKeyProperty]);

  const buildSkillProblem = useCallback((skill: { skill_id: string; title: string }, keyProperty: { id: string; title: string; content: string; examples?: string; explanation?: string }): StoredProblem => {
    const explanationParts = [keyProperty.content, keyProperty.examples, keyProperty.explanation].filter(Boolean) as string[];
    return {
      id: `skill-${skill.skill_id}-${keyProperty.id}`,
      question: keyProperty.title || skill.title,
      answer: keyProperty.content || keyProperty.title || skill.title,
      explanation: explanationParts.join(' ').trim() || keyProperty.title || skill.title,
      createdAt: new Date().toISOString(),
    };
  }, []);

  // ─── Live Tutor handlers ─────────────────────────────────────
  const stopAutoRotation = useCallback(() => {
    setIsTabAutoRotating(false);
    if (autoRotationResumeTimer.current) clearTimeout(autoRotationResumeTimer.current);
    autoRotationResumeTimer.current = setTimeout(() => setIsTabAutoRotating(true), 10000);
  }, []);

  const requestLiveTutorStart = useCallback(() => {
    setLiveTutorStartSignal((prev) => prev + 1);
  }, []);

  const handleCarouselDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    const container = carouselScrollRef.current;
    if (!container) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    carouselDragRef.current = { startX: clientX, startScroll: container.scrollLeft };
    didCarouselDragRef.current = false;
    stopAutoRotation();
  };

  const handleCarouselDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!carouselDragRef.current) return;
    const container = carouselScrollRef.current;
    if (!container) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const delta = carouselDragRef.current.startX - clientX;
    if (Math.abs(delta) > 5) {
      didCarouselDragRef.current = true;
      container.scrollLeft = carouselDragRef.current.startScroll + delta;
    }
  };

  const handleCarouselDragEnd = () => {
    carouselDragRef.current = null;
  };

  const handleSelectLiveTutorProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) { didCarouselDragRef.current = false; return; }
    if (isGuestSessionLocked) {
      openLoginForGuestLimit();
      return;
    }
    stopAutoRotation();
    const selectedProblem = liveTutorProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    setLiveTutorWhiteboardStarted(true);
    requestLiveTutorStart();
  };

  const handleSelectLiveTutorSkill = async (skill: { skill_id: string; title: string }) => {
    if (didCarouselDragRef.current) { didCarouselDragRef.current = false; return; }
    if (isGuestSessionLocked) {
      openLoginForGuestLimit();
      return;
    }
    stopAutoRotation();
    const keyProperty = await fetchSkillKeyProperty(skill.skill_id);
    if (!keyProperty) return;
    const skillProblem = buildSkillProblem(skill, keyProperty);
    setLiveTutorSelection({ kind: 'skill', skillId: skill.skill_id, keyProperty, problem: skillProblem });
    setLiveTutorWhiteboardStarted(true);
    requestLiveTutorStart();
  };

  const handleSelectAmcProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) { didCarouselDragRef.current = false; return; }
    if (isGuestSessionLocked) {
      openLoginForGuestLimit();
      return;
    }
    stopAutoRotation();
    const selectedProblem = amcProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    setLiveTutorWhiteboardStarted(true);
    requestLiveTutorStart();
  };

  const handleSelectHistoryProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) { didCarouselDragRef.current = false; return; }
    if (isGuestSessionLocked) { openLoginForGuestLimit(); return; }
    stopAutoRotation();
    const selectedProblem = historyProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    setLiveTutorWhiteboardStarted(true);
    requestLiveTutorStart();
  };

  const handleSelectScienceProblem = async (problemId: string) => {
    if (didCarouselDragRef.current) { didCarouselDragRef.current = false; return; }
    if (isGuestSessionLocked) { openLoginForGuestLimit(); return; }
    stopAutoRotation();
    const selectedProblem = scienceProblems.find((p) => p.id === problemId);
    if (!selectedProblem) return;
    setLiveTutorSelection({ kind: 'problem', problem: selectedProblem });
    setLiveTutorWhiteboardStarted(true);
    requestLiveTutorStart();
  };

  const handleToggleHomeUploadMenu = () => setIsHomeUploadMenuOpen((prev) => !prev);
  const handlePickHomeImage = () => { setIsHomeUploadMenuOpen(false); homeUploadInputRef.current?.click(); };

  const handleHomeUploadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) { setHomeUploadFile(null); setHomeUploadFileName(null); event.target.value = ''; return; }
    if (!file.type.startsWith('image/')) { setHomeParseErrorMessage('Please upload an image file (jpg, png, etc.)'); setIsHomeParseErrorOpen(true); event.target.value = ''; return; }
    if (file.size > 10 * 1024 * 1024) { setHomeParseErrorMessage('Image size must be under 10MB'); setIsHomeParseErrorOpen(true); event.target.value = ''; return; }
    setHomeUploadFile(file);
    setHomeUploadFileName(file.name);
    event.target.value = '';
  };

  const handleHomeUploadSubmit = async () => {
    if (!user) {
      openLoginForGuestLimit();
      return;
    }
    const trimmedText = homeUploadText.trim();
    if (!trimmedText && !homeUploadFile) return;
    setIsHomeParseLoading(true);
    setHomeParseErrorMessage(null);
    setIsHomeParseErrorOpen(false);
    try {
      const formData = new FormData();
      if (trimmedText) formData.append('text', trimmedText);
      if (homeUploadFile) formData.append('image', homeUploadFile);
      const res = await fetch('/napi/dev/question-input/parse', { method: 'POST', body: formData });
      const data = (await res.json()) as { question?: string; answer?: string; explanation?: string; options?: string[]; type?: string; error?: string };
      if (!res.ok) throw new Error(data.error || 'Failed to parse question');
      const parsedProblem: StoredProblem = {
        id: `home-input-${Date.now()}`,
        question: String(data.question || ''),
        answer: String(data.answer || ''),
        explanation: String(data.explanation || ''),
        options: Array.isArray(data.options) ? data.options.filter((o): o is string => typeof o === 'string') : undefined,
        type: data.type as StoredProblem['type'],
      };
      if (!parsedProblem.question || !parsedProblem.answer) throw new Error('Generated result is incomplete. Please try again.');
      setLiveTutorSelection({ kind: 'problem', problem: parsedProblem });
      setLiveTutorWhiteboardStarted(true);
      requestLiveTutorStart();
      setIsHomeUploadMenuOpen(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      const isFetchError = error instanceof TypeError || /fetch failed/i.test(msg) || /sending request/i.test(msg);
      setHomeParseErrorMessage(isFetchError ? 'Network issue. Please retry.' : (msg || 'Failed to generate question'));
      setIsHomeParseErrorOpen(true);
    } finally {
      setIsHomeParseLoading(false);
    }
  };

  // ─── Live Tutor effects ──────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setHasUsedGuestLiveTutor(localStorage.getItem(LANDING_GUEST_LIVE_TUTOR_USED_KEY) === '1');
  }, []);

  useEffect(() => { fetchLiveTutorProblems(); }, [fetchLiveTutorProblems]);
  useEffect(() => { fetchLiveTutorSkills(); }, [fetchLiveTutorSkills]);
  useEffect(() => { fetchAmcProblems(); }, [fetchAmcProblems]);
  useEffect(() => { fetchHistoryProblems(); }, [fetchHistoryProblems]);
  useEffect(() => { fetchScienceProblems(); }, [fetchScienceProblems]);

  // Auto-select the problem that matches the preview board entries
  useEffect(() => {
    if (liveTutorProblems.length > 0 && !liveTutorSelection) {
      const previewProblem = liveTutorProblems.find((p) => p.id === 'imported-1770007445483') ?? liveTutorProblems[0];
      setLiveTutorSelection({ kind: 'problem', problem: previewProblem });
    }
  }, [liveTutorProblems, liveTutorSelection]);

  useEffect(() => {
    if (liveTutorMathSkills.length === 0) return;
    const skillIds = liveTutorMathSkills.map((s) => s.skill_id);
    let cancelled = false;
    const run = () => { if (!cancelled) void prefetchSkillKeyProperties(skillIds); };
    const win = window as Window & { requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number; cancelIdleCallback?: (h: number) => void };
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;
    if (typeof win.requestIdleCallback === 'function') { idleHandle = win.requestIdleCallback(() => run(), { timeout: 1200 }); }
    else { timeoutHandle = window.setTimeout(run, 250); }
    return () => { cancelled = true; if (idleHandle !== null && typeof win.cancelIdleCallback === 'function') win.cancelIdleCallback(idleHandle); if (timeoutHandle !== null) window.clearTimeout(timeoutHandle); };
  }, [liveTutorMathSkills, prefetchSkillKeyProperties]);

  // Tab auto-rotation
  useEffect(() => {
    if (!isTabAutoRotating) return;
    const tabOrder: Array<'problems' | 'skills' | 'amc8' | 'history' | 'science'> = ['problems', 'skills', 'amc8', 'history', 'science'];
    const timer = window.setTimeout(() => {
      setLiveTutorActiveTab((currentTab) => {
        const idx = tabOrder.indexOf(currentTab);
        return tabOrder[(idx + 1) % tabOrder.length];
      });
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [liveTutorActiveTab, isTabAutoRotating]);

  // Carousel auto-scroll
  useEffect(() => {
    const container = carouselScrollRef.current;
    const dataLength = liveTutorActiveTab === 'problems' ? liveTutorProblems.length : liveTutorActiveTab === 'skills' ? liveTutorMathSkills.length : liveTutorActiveTab === 'history' ? historyProblems.length : liveTutorActiveTab === 'science' ? scienceProblems.length : amcProblems.length;
    if (!container || dataLength === 0) return;

    let oneSetWidth = container.scrollWidth / 3;
    const measureOneSetWidth = () => { oneSetWidth = container.scrollWidth / 3; };
    const normalizeScrollLeft = (rawScrollLeft: number) => {
      if (oneSetWidth <= 0) return rawScrollLeft;
      let next = rawScrollLeft;
      while (next >= oneSetWidth * 2) next -= oneSetWidth;
      while (next < 0) next += oneSetWidth;
      return next;
    };

    if (lastCarouselTabRef.current !== liveTutorActiveTab || lastCarouselDataLenRef.current !== dataLength) {
      container.scrollLeft = oneSetWidth;
      lastCarouselTabRef.current = liveTutorActiveTab;
      lastCarouselDataLenRef.current = dataLength;
    }

    let frameId = 0;
    let lastTime = 0;
    let resizeObserver: ResizeObserver | null = null;
    const speed = liveTutorActiveTab === 'skills' ? -0.04 : 0.04;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => measureOneSetWidth());
      resizeObserver.observe(container);
    }

    const animate = (time: number) => {
      if (carouselDragRef.current) { lastTime = 0; frameId = window.requestAnimationFrame(animate); return; }
      if (!lastTime) lastTime = time;
      const delta = Math.min(time - lastTime, 100);
      lastTime = time;
      container.scrollLeft = normalizeScrollLeft(container.scrollLeft + delta * speed);
      frameId = window.requestAnimationFrame(animate);
    };
    frameId = window.requestAnimationFrame(animate);

    return () => { window.cancelAnimationFrame(frameId); resizeObserver?.disconnect(); };
  }, [liveTutorActiveTab, liveTutorProblems.length, liveTutorMathSkills.length, amcProblems.length, historyProblems.length, scienceProblems.length]);

  // Close home upload menu on outside click
  useEffect(() => {
    if (!isHomeUploadMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (homeUploadMenuRef.current && e.target instanceof Node && !homeUploadMenuRef.current.contains(e.target)) setIsHomeUploadMenuOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsHomeUploadMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('keydown', handleEscape); };
  }, [isHomeUploadMenuOpen]);

  // 计算元素基于视口位置的背景渐变透明度
  const calculatePageOpacity = (elementRef: React.RefObject<HTMLDivElement | null>) => {
    if (!elementRef.current) return 0;

    const rect = elementRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const elementTop = rect.top;
    const elementBottom = rect.bottom;

    // 当元素完全在视口下方时，背景为白色
    if (elementTop >= viewportHeight) {
      return 0;
    }

    // 当元素顶部出现在视口底部时，开始从白色到蓝色渐变
    if (elementTop < viewportHeight && elementTop > 0) {
      // 从元素顶部出现在视口底部到元素顶部到达视口顶部，渐变进度
      const progress = (viewportHeight - elementTop) / viewportHeight;
      return Math.max(0, Math.min(1, progress));
    }

    // 当元素顶部到达视口顶部时，保持蓝色
    if (elementTop <= 0 && elementBottom > viewportHeight) {
      return 1; // 完全蓝色
    }

    // 当元素顶部已经超过视口顶部，但元素底部还在视口内时，开始从蓝色渐变回白色
    if (elementTop <= 0 && elementBottom <= viewportHeight && elementBottom > 0) {
      // 继续滚动一个屏幕高度的距离完全变成白色
      const progress = elementBottom / viewportHeight;
      return Math.max(0, Math.min(1, progress));
    }

    // 元素完全离开视口上方时，背景为白色
    if (elementBottom <= 0) {
      return 0;
    }

    return 0; // 默认白色
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!mainLogoRef.current) return;
      const rect = mainLogoRef.current.getBoundingClientRect();
      setShowNavLogo(rect.bottom < 0);

      // Hide arrow when user scrolls down more than 10px
      setShowScrollArrow(window.scrollY <= 30);

      // 计算各页面的背景透明度
      setPage2Opacity(calculatePageOpacity(page2Ref));

      setPage5Opacity(calculatePageOpacity(page5Ref));
      setPage7Opacity(calculatePageOpacity(page7Ref));

      // Check if page 5 is in viewport and load Rive animations if needed
      if (!shouldLoadRive && page5Ref.current) {
        const rect5 = page5Ref.current.getBoundingClientRect();
        // Load Rive when page 5 is about to come into view (with 500px buffer)
        if (rect5.top < window.innerHeight + 500) {
          setShouldLoadRive(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [shouldLoadRive]);

  useEffect(() => {
    console.log('[LandingClient] useEffect triggered');
    // Check if there's a flag to show login modal (from homework share link redirect)
    const shouldShowModal = sessionStorage.getItem('show_login_modal') === 'true';
    console.log('[LandingClient] shouldShowModal:', shouldShowModal);
    console.log('[LandingClient] flowquiz_redirect:', sessionStorage.getItem('flowquiz_redirect'));

    // 读取URL参数中的邀请码和分享链接
    // https://pengi.ai/game?game=crossy-road&roomId=KEW6OD
    // https://pengi.ai?code=JQI384
    // https://pengi.ai?redirect=/game?game=crossy-road&roomId=KEW6OD
    // https://pengi.ai?redirect=/fish/d9db0446-6358-4609-be7a-ba2c65c9a1dc
    // https://pengi.ai?redirect=/pet-card/123
    const urlParams = new URLSearchParams(window.location.search);
    let code = urlParams.get('code');
    let game = urlParams.get('game');
    let roomId = urlParams.get('roomId');
    let isFishShare = false;
    let isPetCardShare = false;
    let isMinipetShare = false;

    // 检查redirect参数中是否包含分享链接的参数或fish/pet-card/magic-lab路径
    const redirectUrl = urlParams.get('redirect');
    if (redirectUrl && !game && !roomId && !code) {
      try {
        const redirectUrlObj = new URL(redirectUrl, window.location.origin);
        const redirectParams = new URLSearchParams(redirectUrlObj.search);
        game = redirectParams.get('game');
        roomId = redirectParams.get('roomId');
        code = redirectParams.get('code');

        // 检查是否是fish分享链接
        if (redirectUrlObj.pathname.startsWith('/fish/')) {
          isFishShare = true;
        }

        // 检查是否是pet-card分享链接
        if (redirectUrlObj.pathname.startsWith('/pet-card/')) {
          isPetCardShare = true;
        }

        // 检查是否是minipet分享链接
        if (redirectUrlObj.pathname.startsWith('/magic-lab/')) {
          isMinipetShare = true;
        }
      } catch (error) {
        console.error('Error parsing redirect URL:', error);
      }
    }

    const showLogin = urlParams.get('show_login') === '1';
    if (code || game || roomId || isFishShare || isPetCardShare || isMinipetShare || shouldShowModal || showLogin) {
      setIsFromShare(true);
      console.log('isFromShare', isFromShare);

      // Clear the flag after reading it (one-time use)
      if (shouldShowModal) {
        sessionStorage.removeItem('show_login_modal');
      }

      // 等待页面渲染完毕后弹出登录弹窗
      const showModalAfterRender = () => {
        // 使用requestAnimationFrame确保DOM已经渲染
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setLoginModalVisible(true);
          });
        });
      };

      // 检查页面是否已经完全加载
      if (document.readyState === 'complete') {
        showModalAfterRender();
      } else {
        // 等待页面加载完成
        window.addEventListener('load', showModalAfterRender, { once: true });
      }
    }
    if (code) {
      // 用户携带了分享邀请码进来
      report(ReportEvent.SHARE_INVITE_CODE, {
        code: code
      });

      // 埋点：追踪用户使用旧格式链接 (pengi.ai?code=xxx)
      report(ReportEvent.REFERRAL_LINK_CLICK, {
        code: code,
        link_format: 'query_parameter',
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      // 同时记录到 Supabase
      const logReferralClick = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();

          await supabase.from('referral_link_clicks').insert({
            referral_code: code,
            user_id: user?.id || null,
            user_email: user?.email || null,
            link_format: 'query_parameter',
            user_agent: navigator.userAgent,
            referer: document.referrer || null,
            session_id: sessionStorage.getItem('pengi_session_id') || null
          });
        } catch (error) {
          console.error('Failed to log referral link click:', error);
        }
      };

      logReferralClick();
      setInviteCode(code);
    }
  }, [isFromShare]);

  // 自动切换语音评价的useEffect
  useEffect(() => {
    const startAutoSwitch = () => {
      voiceIntervalRef.current = setInterval(() => {
        setCurrentVoiceIndex((prev) => (prev + 1) % realVoiceList.length);
      }, 4000); // 统一使用4秒间隔
    };

    startAutoSwitch();

    return () => {
      if (voiceIntervalRef.current) {
        clearInterval(voiceIntervalRef.current);
      }
      if (voiceTimeoutRef.current) {
        clearTimeout(voiceTimeoutRef.current);
      }
    };
  }, []);

  // 自动切换平台标签的useEffect
  useEffect(() => {
    const startPlatformAutoSwitch = () => {
      platformIntervalRef.current = setInterval(() => {
        setCurrentPlatformIndex((prev) => (prev + 1) % platformList.length);
      }, 4000); // 4秒间隔
    };

    startPlatformAutoSwitch();

    return () => {
      if (platformIntervalRef.current) {
        clearInterval(platformIntervalRef.current);
      }
      if (platformTimeoutRef.current) {
        clearTimeout(platformTimeoutRef.current);
      }
    };
  }, []);

  // 自动滚动到当前选中的testimonial卡片
  useEffect(() => {
    if (testimonialScrollRef.current) {
      const container = testimonialScrollRef.current;
      const gap = 16; // gap-4 = 16px (根据用户修改)
      const padding = 32; // p-8 = 32px

      // 根据屏幕宽度确定卡片宽度
      let cardWidth = 350; // 默认和移动端宽度
      if (window.innerWidth >= 1024) { // lg断点
        cardWidth = 550;
      } else if (window.innerWidth >= 640) { // sm断点
        cardWidth = 350;
      }

      // 计算最大滚动距离
      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);

      let targetScrollLeft = 0;

      // 对于第一个卡片，确保从容器开始位置显示
      if (currentVoiceIndex === 0) {
        targetScrollLeft = 0;
      }
      // 对于最后一个卡片，确保能完全显示在容器末尾
      else if (currentVoiceIndex === realVoiceList.length - 1) {
        targetScrollLeft = maxScrollLeft;
      }
      // 对于中间的卡片，计算居中位置
      else {
        // 计算选中卡片的左边缘位置（相对于滚动容器内容）
        const selectedCardLeft = currentVoiceIndex * (cardWidth + gap);

        // 计算选中卡片的中心位置
        const selectedCardCenter = selectedCardLeft + (cardWidth / 2);

        // 计算容器可视区域的中心位置
        const containerCenter = container.clientWidth / 2;

        // 计算目标滚动位置，让选中的卡片在容器中心
        // 需要减去padding，因为scrollLeft是相对于padding内部的
        targetScrollLeft = selectedCardCenter + padding - containerCenter;

        // 确保不超出滚动边界
        targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
      }

      // 平滑滚动到目标位置
      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    }
  }, [currentVoiceIndex]);

  // 使用 useRef 存储偏移量，避免重置
  const scrollOffsetRef = useRef(0);

  // 无限滚动动画 - 使用 useRef 避免状态更新导致的卡顿
  useEffect(() => {
    const imageWidth = 305;
    const totalWidth = school_photo.length * imageWidth;
    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      if (!isHoveringPhotos && schoolPhotoContainerRef.current) {
        // 计算时间差以确保稳定的动画速度
        const deltaTime = currentTime - lastTime;

        // 基于时间的移动，确保稳定速度（30像素/秒）
        scrollOffsetRef.current -= (30 * deltaTime) / 1000;

        // 无缝循环：当滚动完一组图片时，平滑重置
        if (Math.abs(scrollOffsetRef.current) >= totalWidth) {
          scrollOffsetRef.current = scrollOffsetRef.current % totalWidth;
        }

        // 直接操作 DOM，避免 React 重渲染
        const scrollGroups = schoolPhotoContainerRef.current.querySelectorAll('.scroll-group');
        scrollGroups.forEach((group, index) => {
          const groupOffset = scrollOffsetRef.current + index * totalWidth;
          (group as HTMLElement).style.transform = `translateX(${groupOffset}px)`;
        });
      }

      // 更新 lastTime，确保暂停后继续时时间计算正确
      lastTime = currentTime;
      scrollAnimationRef.current = requestAnimationFrame(animate);
    };

    scrollAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, [isHoveringPhotos]);

  // 监听窗口大小变化，重新计算滚动位置
  useEffect(() => {
    const handleResize = () => {
      // 延迟执行以确保DOM已更新
      setTimeout(() => {
        if (testimonialScrollRef.current) {
          const container = testimonialScrollRef.current;
          const gap = 16; // gap-4 = 16px
          const padding = 32; // p-8 = 32px

          let cardWidth = 350;
          if (window.innerWidth >= 1024) {
            cardWidth = 550;
          } else if (window.innerWidth >= 640) {
            cardWidth = 350;
          }

          const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
          let targetScrollLeft = 0;

          if (currentVoiceIndex === 0) {
            targetScrollLeft = 0;
          } else if (currentVoiceIndex === realVoiceList.length - 1) {
            targetScrollLeft = maxScrollLeft;
          } else {
            const selectedCardLeft = currentVoiceIndex * (cardWidth + gap);
            const selectedCardCenter = selectedCardLeft + (cardWidth / 2);
            const containerCenter = container.clientWidth / 2;
            targetScrollLeft = selectedCardCenter + padding - containerCenter;
            targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
          }

          container.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // 初始计算
    return () => window.removeEventListener('resize', handleResize);
  }, [currentVoiceIndex]);

  // 自动滚动到当前选中的平台标签
  useEffect(() => {
    if (platformScrollContainerRef.current) {
      const container = platformScrollContainerRef.current;
      const tabWidth = 350; // 每个tab的宽度
      const gap = 32; // gap-8 = 32px

      // 获取当前padding值
      let paddingValue = 16; // px-4 = 16px (移动端)
      if (window.innerWidth >= 1024) { // lg断点
        paddingValue = 64; // px-16 = 64px
      } else if (window.innerWidth >= 768) { // md断点
        paddingValue = 32; // px-8 = 32px
      }

      // 计算选中tab的中心位置（考虑padding）
      const selectedTabCenter = paddingValue + currentPlatformIndex * (tabWidth + gap) + (tabWidth / 2);

      // 计算容器的中心位置
      const containerCenter = container.clientWidth / 2;

      // 计算目标滚动位置，让选中的tab在容器中心
      const targetScrollLeft = selectedTabCenter - containerCenter;

      // 确保滚动位置在有效范围内
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft)) - 25;

      // 平滑滚动到目标位置
      container.scrollTo({
        left: finalScrollLeft,
        behavior: 'smooth'
      });
    }
  }, [currentPlatformIndex]);

  // 处理平台标签手动点击切换
  const handlePlatformIndexChange = (index: number) => {
    // 清除当前的自动切换定时器和延迟定时器
    if (platformIntervalRef.current) {
      clearInterval(platformIntervalRef.current);
      platformIntervalRef.current = null;
    }
    if (platformTimeoutRef.current) {
      clearTimeout(platformTimeoutRef.current);
      platformTimeoutRef.current = null;
    }

    // 设置新的平台索引
    setCurrentPlatformIndex(index);

    // 重新启动自动切换，使用统一的4秒间隔
    platformTimeoutRef.current = setTimeout(() => {
      platformIntervalRef.current = setInterval(() => {
        setCurrentPlatformIndex((prev) => (prev + 1) % platformList.length);
      }, 4000); // 统一使用4秒间隔
      platformTimeoutRef.current = null; // 清除已完成的timeout引用
    }, 4000); // 手动点击后4秒重新开始自动切换
  };

  // 处理手动点击切换
  const handleVoiceIndexChange = (index: number) => {
    // 清除当前的自动切换定时器和延迟定时器
    if (voiceIntervalRef.current) {
      clearInterval(voiceIntervalRef.current);
      voiceIntervalRef.current = null;
    }
    if (voiceTimeoutRef.current) {
      clearTimeout(voiceTimeoutRef.current);
      voiceTimeoutRef.current = null;
    }

    setCurrentVoiceIndex(index);

    // 重新启动自动切换，使用统一的4秒间隔
    voiceTimeoutRef.current = setTimeout(() => {
      voiceIntervalRef.current = setInterval(() => {
        setCurrentVoiceIndex((prev) => (prev + 1) % realVoiceList.length);
      }, 4000); // 统一使用4秒间隔
      voiceTimeoutRef.current = null; // 清除已完成的timeout引用
    }, 4000); // 手动点击后4秒重新开始自动切换
  };

  // 处理左箭头点击
  const handlePrevVoice = () => {
    const prevIndex = currentVoiceIndex === 0 ? realVoiceList.length - 1 : currentVoiceIndex - 1;
    handleVoiceIndexChange(prevIndex);
  };

  // 处理右箭头点击
  const handleNextVoice = () => {
    const nextIndex = (currentVoiceIndex + 1) % realVoiceList.length;
    handleVoiceIndexChange(nextIndex);
  };

  // 检测是否为移动设备
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // 处理视频播放，在移动端启用全屏
  const handleVideoPlay = () => {
    if (!videoLoaded) {
      setVideoLoaded(true);
      // Give time for video to load
      setTimeout(() => {
        setVideoPlaying(true);
      }, 100);
    } else {
      setVideoPlaying(true);
    }

    // 在移动端延迟触发全屏
    if (isMobileDevice()) {
      setTimeout(() => {
        const videoElement = videoPlayerRef.current?.getInternalPlayer();
        if (videoElement && videoElement.requestFullscreen) {
          videoElement.requestFullscreen().catch((err: any) => {
            console.log('Fullscreen request failed:', err);
          });
        } else if (videoElement && (videoElement as any).webkitRequestFullscreen) {
          (videoElement as any).webkitRequestFullscreen();
        } else if (videoElement && (videoElement as any).mozRequestFullScreen) {
          (videoElement as any).mozRequestFullScreen();
        } else if (videoElement && (videoElement as any).msRequestFullscreen) {
          (videoElement as any).msRequestFullscreen();
        }
      }, 100);
    }

    report(ReportEvent.USER_LANDING_VIDEO_PLAY_CLICK, {});
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log(name, email, message);
    if (name === '' || email === '' || message === '') {
      alert('Please fill in all fields');
      return;
    }

    const { data, error } = await supabase.from('contact_us').insert({
      name,
      email,
      message
    });

    if (!error) {
      alert('Message sent successfully');
    }
  }

  const handleGetFreeAccess = async () => {
    // Store email in landing_email table if email is provided
    if (loginEmail && loginEmail.trim() !== '') {
      try {
        const { error } = await supabase.from('landing_email').insert({
          email: loginEmail.trim()
        });

        if (error && error.code !== '23505') { // Ignore duplicate key errors
          console.error('Error storing email:', error);
        }
      } catch (err) {
        console.error('Error storing email:', err);
      }
    }
  }

  return (
    <div className="w-full h-full overflow-y-auto overflow-x-hidden flex flex-col items-center bg-white">
      <div className="fixed top-0 left-0 w-full h-[60px] z-50 bg-white flex items-center justify-center" style={{ borderBottom: '1px solid #D7D9E0', backgroundColor: '#ffffff', opacity: showNavLogo ? 1 : 0 }}>
        <div className="flex flex-1 flex-row justify-between h-[60px] items-center px-3 md:px-8" style={{ maxWidth: landing_width }}>
          <div className="flex flex-row items-center h-full transition-opacity duration-500"
            style={{ opacity: showNavLogo ? 1 : 0 }}>
            <Image className="z-10" src="/assets/landing-logo.png" alt="penguin" width={40} height={40} optimizedWidth={80} />
            <div className="emphasize-text ml-5 font-bold text-2xl rounded-full flex items-center justify-center" style={{ color: '#735648' }}>
              Pengi
            </div>
          </div>
          <NormalButton onClick={() => setLoginModalVisible(true)}>
            Get Started
          </NormalButton>
        </div>
      </div>

      {/* 第一页 */}
      <div className="relative min-h-screen w-screen flex items-center justify-center">
        <div className="relative flex flex-col items-center w-full max-w-[1200px] px-4">
          <div className="flex-1 w-full flex flex-col items-center -mt-10">
            <div className="flex flex-row items-center">
              <Image className="z-10" src="/assets/landing-logo.png" alt="penguin" width={50} height={50} optimizedWidth={100} priority />
              <div className="emphasize-text ml-5 font-bold text-3xl text-[#1F2A44] rounded-full flex items-center justify-center">
                Meet Pengi
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center justify-center mt-10 gap-4 px-4 lg:px-16">
              <div className="flex flex-[1] flex-col justify-center text-center lg:text-left">
                {/* SEO优化的H1标签 - 对搜索引擎可见，对用户隐藏 */}
                <h1 className="sr-only">Welcome to Pengi.ai - Interactive Learning for K12 Students</h1>
                {/* 保持原有的品牌标语作为视觉标题 */}
                <div className="text-[40px] sm:text-[56px]" style={{ fontFamily: "var(--font-sedgwick-ave)" }}>No More Nagging.</div>
                <h2 className="display-text-bold text-[20px] sm:text-[34px]">Just Self-Motivated Learning</h2>
                <p className="display-text text-base sm:text-xl mt-6 leading-relaxed" style={{ color: '#4D5059' }}>
                  <span className="display-text-bold" style={{ color: '#42C52D' }}>Aligned</span>{' · '}
                  <span className="display-text-bold" style={{ color: '#FBAF2C' }}>Engaging</span>{' · '}
                  <span className="display-text-bold" style={{ color: '#8B8CFD' }}>Assured</span>
                  {' — '}a learning platform where kids ask questions, explore at their own pace, and build real understanding.
                </p>
              </div>
              <div className="flex flex-[1] flex-col items-center justify-center z-10">
                <div className="relative w-[95%] aspect-video">
                  {/* Video player - lazy loaded */}
                  {videoLoaded && (
                    <div className={`absolute inset-0 w-full h-full ${!videoPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                      <ReactPlayer
                        ref={videoPlayerRef}
                        url="https://syllabus-cdn.pengi.ai/other/pengi_video_6.mp4"
                        controls
                        playing={videoPlaying}
                        width="100%"
                        height="100%"
                        className="rounded-2xl"
                        light={false}
                        playsinline
                        config={{
                          file: {
                            attributes: {
                              style: { borderRadius: '20px' },
                              playsInline: true,
                              'webkit-playsinline': 'true',
                              preload: 'none'
                            },
                            forceVideo: true
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Video cover overlay - always visible when not playing */}
                  {!videoPlaying && (
                    <div className="absolute inset-0 rounded-2xl overflow-hidden cursor-pointer z-10"
                      onClick={handleVideoPlay}>
                      <Image
                        src="/assets/newlanding_cover.png"
                        alt="Video Cover"
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 95vw, (max-width: 1200px) 50vw, 600px"
                        priority
                        optimizedWidth={800}
                      />
                      {/* Play button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 bg-[#DCF4FE] rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all duration-200 shadow-lg">
                          <Play className="w-8 h-8" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="display-text text-sm sm:text-base flex flex-row flex-nowrap items-center w-full justify-center gap-2 sm:gap-3 mt-5 text-[#9CA3AF]">
                  <span className="border border-[#D7D9E0] px-2 sm:px-3 py-1 rounded-full">Math</span>
                  <span className="border border-[#D7D9E0] px-2 sm:px-3 py-1 rounded-full">Language Arts</span>
                  <span className="border border-[#D7D9E0] px-2 sm:px-3 py-1 rounded-full">Science</span>
                  <span className="border border-[#D7D9E0] px-2 sm:px-3 py-1 rounded-full">Social Studies</span>
                </div>
              </div>
            </div>

            <div className="flex flex-row items-center gap-3 mt-8">
              <input
                type="email"
                placeholder="Enter your email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleGetFreeAccess();
                    setLoginModalVisible(true);
                  }
                }}
                className="pl-4 pr-15 py-3 rounded-full border border-[#D7D9E0] focus:outline-none focus:ring-2 focus:ring-[#42C52D] focus:border-transparent text-base w-[200px] sm:w-[350px] bg-white"
              />
              <div className="-ml-15 mt-6" ref={mainLogoRef}>
                <NormalButton
                  onClick={() => {
                    handleGetFreeAccess();
                    setLoginModalVisible(true);
                  }}
                  className="z-10 w-[160px] sm:w-[200px] lg:w-[230px]"
                >
                  <span className="text-base sm:text-lg lg:text-xl py-[5px]">Get Free Access</span>
                </NormalButton>
                <div className="display-text text-sm font-bold mt-1 text-center" style={{ color: '#735648' }}>No credit card needed</div>
              </div>
            </div>

            {/* Bouncing down arrow - floating at the bottom of first screen */}
            {showScrollArrow && (
              <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 z-50 animate-bounce">
                <ChevronDown className="w-[40px] h-[40px] md:w-[60px] md:h-[60px] lg:w-[70px] lg:h-[70px]" color={'#42C52D'} strokeWidth={2.5} />
              </div>
            )}

            <div className="absolute hidden lg:block" style={{ top: '-40px', left: '50px' }}>
              <Image src="/assets/landing-sun.png" alt="landing-sun" width={116} height={116} loading="lazy" optimizedWidth={200} />
            </div>
            <div className="absolute hidden lg:block" style={{ bottom: '10px', right: '100px' }}>
              <Image src="/assets/landing-star.png" alt="landing-star" width={110} height={100} loading="lazy" optimizedWidth={200} />
            </div>
          </div>
        </div>
      </div>

      {/* 延迟加载第2-8页，优化首屏性能 */}
      {belowFoldEnabled && (
        <>
          {/* 第二页 */}
          <div ref={page2Ref} className="min-h-screen w-screen flex items-center justify-center overflow-x-hidden" style={{ backgroundColor: interpolateColor(page2Opacity) }}>
            <div className="flex flex-col xl:flex-row items-center justify-center w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
              <div className="relative flex w-full max-w-[650px] h-[420px] sm:h-[560px] md:h-[620px] lg:h-[660px] flex-shrink-0 items-center justify-center">
                {/* 中心图片 */}
                <Image
                  src="https://syllabus-cdn.pengi.ai/assets/newlanding_1.png?v=2"
                  alt="newlanding_1"
                  width={300}
                  height={300}
                  className="w-[180px] sm:w-[240px] md:w-[280px] lg:w-[300px] h-auto z-10 object-contain"
                  loading="lazy"
                  sizes="300px"
                  optimizedWidth={500}
                />

                {/* 第1个div - 现在是原来的第2个 (上方位置) */}
                <div
                  className="absolute w-[130px] sm:w-[150px] md:w-[160px] rounded-2xl border border-[#D7D9E0] shadow-lg bg-white display-text text-base text-black flex flex-col items-center text-center px-2"
                  style={{
                    top: '6%',
                    left: '50%',
                    transform: 'translateX(-50%) rotate(0deg)',
                    zIndex: 10,
                  }}
                >
                  <Image
                    src="/assets/newlanding_good.png"
                    alt="newlanding_good"
                    width={80}
                    height={80}
                    className="h-[56px] w-[56px] min-[420px]:h-[64px] min-[420px]:w-[64px] sm:h-[72px] sm:w-[72px] md:h-[80px] md:w-[80px] -mt-7 min-[420px]:-mt-8 md:-mt-10 object-contain"
                    loading="lazy"
                    optimizedWidth={160}
                  />
                  <div className="display-text font-medium text-sm sm:text-base text-[#4D5059] mt-2 mb-4 leading-snug">Hard to keep motivated?</div>
                </div>

                {/* 第2个div - 现在是原来的第3个 (右上方位置) */}
                <div
                  className="absolute w-[130px] sm:w-[150px] md:w-[160px] rounded-2xl border border-[#D7D9E0] shadow-lg bg-white display-text text-base text-black flex flex-col items-center text-center px-2"
                  style={{
                    top: '26%',
                    right: '2%',
                    transform: 'rotate(7deg)',
                    zIndex: 10,
                  }}
                >
                  <Image
                    src="/assets/newlanding_focus.png"
                    alt="newlanding_focus"
                    width={80}
                    height={80}
                    className="h-[56px] w-[56px] min-[420px]:h-[64px] min-[420px]:w-[64px] sm:h-[72px] sm:w-[72px] md:h-[80px] md:w-[80px] -mt-7 min-[420px]:-mt-8 md:-mt-10 object-contain"
                    loading="lazy"
                    optimizedWidth={160}
                  />
                  <div className="display-text font-medium text-sm sm:text-base text-[#4D5059] mt-2 mb-4 leading-snug">Fear of <br /> distraction?</div>
                </div>

                {/* 第3个div - 现在是原来的第4个 (右下方位置，距离更远) */}
                <div
                  className="absolute w-[130px] sm:w-[150px] md:w-[160px] rounded-2xl border border-[#D7D9E0] shadow-lg bg-white display-text text-base text-black flex flex-col items-center text-center px-2"
                  style={{
                    bottom: '14%',
                    right: '4%',
                    transform: 'rotate(-8deg)',
                    zIndex: 10,
                  }}
                >
                  <Image
                    src="/assets/newlanding_alarm.png"
                    alt="newlanding_alarm"
                    width={80}
                    height={80}
                    className="h-[56px] w-[56px] min-[420px]:h-[64px] min-[420px]:w-[64px] sm:h-[72px] sm:w-[72px] md:h-[80px] md:w-[80px] -mt-7 min-[420px]:-mt-8 md:-mt-10 object-contain"
                    loading="lazy"
                    optimizedWidth={160}
                  />
                  <div className="display-text font-medium text-sm sm:text-base text-[#4D5059] mt-2 mb-4 leading-snug">Too busy <br /> to help?</div>
                </div>

                {/* 第4个div - 现在是原来的第5个 (左下方位置，距离更远) */}
                <div
                  className="absolute w-[140px] sm:w-[165px] md:w-[180px] rounded-2xl border border-[#D7D9E0] shadow-lg bg-white display-text text-base text-black flex flex-col items-center text-center px-2"
                  style={{
                    bottom: '14%',
                    left: '2%',
                    transform: 'rotate(8deg)',
                    zIndex: 10,
                  }}
                >
                  <Image
                    src="/assets/newlanding_missing.png"
                    alt="newlanding_missing"
                    width={80}
                    height={80}
                    className="h-[56px] w-[56px] min-[420px]:h-[64px] min-[420px]:w-[64px] sm:h-[72px] sm:w-[72px] md:h-[80px] md:w-[80px] -mt-7 min-[420px]:-mt-8 md:-mt-10 object-contain"
                    loading="lazy"
                    optimizedWidth={160}
                  />
                  <div className="display-text font-medium text-sm sm:text-base text-[#4D5059] mt-2 mb-4 leading-snug">No idea of <br /> your child's gaps?</div>
                </div>

                {/* 第5个div - 现在是原来的第1个 (左上方位置) */}
                <div
                  className="absolute w-[140px] sm:w-[165px] md:w-[180px] rounded-2xl border border-[#D7D9E0] shadow-lg bg-white display-text text-base text-black flex flex-col items-center text-center px-2"
                  style={{
                    top: '26%',
                    left: '1%',
                    transform: 'rotate(-7deg)',
                    zIndex: 10,
                  }}
                >
                  <Image
                    src="/assets/newlanding_bookShelf.png"
                    alt="newlanding_bookShelf"
                    width={80}
                    height={80}
                    className="h-[56px] w-[56px] min-[420px]:h-[64px] min-[420px]:w-[64px] sm:h-[72px] sm:w-[72px] md:h-[80px] md:w-[80px] -mt-7 min-[420px]:-mt-8 md:-mt-10 object-contain"
                    loading="lazy"
                    optimizedWidth={160}
                  />
                  <div className="display-text font-medium text-sm sm:text-base text-[#4D5059] mt-2 mb-6 leading-snug">Hard to find quality learning materials?</div>
                </div>
                {/* Pengi logo + tagline at bottom of left column */}
                <div className="absolute -bottom-3 min-[420px]:-bottom-1 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-row items-center gap-3 z-10 border border-[#D7D9E0] px-3 sm:px-4 py-1.5 rounded-full">
                  <Image src={'/assets/landing-logo.png'} alt="landing-logo" width={40} height={40} optimizedWidth={100} />
                  <div className="display-text-semi text-sm sm:text-xl text-[#4D5059] whitespace-nowrap">We Get It.</div>
                </div>
              </div>
              <div className="w-full xl:flex-1 flex flex-col items-center justify-center mt-6 xl:mt-0">
                <div className="w-full max-w-[680px] flex flex-col items-center justify-center relative display-text text-[#4D5059] text-center my-4 sm:my-10 font-medium px-2" style={{ lineHeight: '1.45' }}>
                  <div className="absolute -top-5 left-0 hidden xl:block z-0" style={{ transform: 'scale(1, 1.98) translateX(-2%) translateY(12px)' }}>
                    <Image src="/assets/newlanding_import.png" alt="newlanding_import" width={800} height={300} className="w-[1000px]" optimizedWidth={500} />
                  </div>
                  <div className="mt-4 z-10 max-w-full text-[clamp(22px,3.6vw,44px)] leading-[1.2]">
                    <span className="font-bold" style={{ color: '#42C52D' }}>Duolingo</span>, but for Schoolwork
                  </div>
                  <div className="display-text text-xl sm:text-2xl text-[#9CA3AF] my-1 z-10">+</div>
                  <div className="z-10 text-[clamp(22px,3.3vw,40px)] leading-[1.2]">
                    Live AI <span className="font-bold" style={{ color: '#42C52D' }}>Tutor</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 第三页 – Live Tutor Section ──────────────────────────────── */}
          <div className="relative w-screen" style={{ backgroundColor: '#FFFFFF' }}>
            {/* Top curved edge */}
            <div
              className={`pointer-events-none absolute top-0 left-0 right-0 z-0 w-full overflow-hidden ${liveTutorWhiteboardStarted ? 'hidden md:block' : ''
                }`}
              style={{ height: '60px', transform: 'translateY(-59px)' }}
            >
              <svg
                viewBox="0 0 1200 60"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
                className="w-full h-full"
              >
                <path
                  d="M 0 0 Q 600 60 1200 0 L 1200 60 L 0 60 Z"
                  fill="#FFFFFF"
                />
              </svg>
            </div>

            <div className="relative z-20 mx-auto w-full max-w-[1200px] px-4 pb-2 pt-8 sm:px-6 md:px-8 mb-2">
              <div className="display-text-semi text-4xl font-bold w-full text-center">
                <span className="display-text-bold inline-block whitespace-nowrap" style={{ color: '#42C52D', fontSize: '50px' }}>Live Tutor</span>
                <span className="text-black block whitespace-nowrap">in Action</span>
              </div>
              <div className="display-text text-base tracking-wide w-full text-center mt-3" style={{ color: '#4D5059' }}>A Thinking Partner, Not an Answer Machine. Try it live with your personal tutor using voice.</div>
            </div>

            {/* Whiteboard */}
            {liveTutorWhiteboardStarted && (
              <div className="relative">
                <LiveWhiteboard
                  selection={liveTutorSelection}
                  isGuestSessionLocked={isGuestSessionLocked}
                  onRequireAuth={openLoginForGuestLimit}
                  onProblemCompleted={markGuestLiveTutorUsed}
                  onSessionEnd={() => setIsTabAutoRotating(true)}
                  startSessionSignal={liveTutorStartSignal}
                  previewEntries={PREVIEW_BOARD_ENTRIES}
                  desktopWidth={700}
                  desktopHeight={478}
                />
                <Image
                  src="tutor/whiteboard-pengi.png"
                  alt=""
                  width={180}
                  height={180}
                  optimizedWidth={400}
                  className="pointer-events-none absolute bottom-2 hidden md:block md:w-[170px] lg:w-[180px]"
                  style={{ zIndex: 25, right: 'calc(50% - 515px)' }}
                />
              </div>
            )}

            {/* Tab Pills + Carousel + Input */}
            <div ref={liveTutorSectionRef}>
              <div className="mx-auto w-full max-w-[1100px] px-4 sm:px-6 md:px-8 pb-8">
                {/* Tab pill bar */}
                <div className={`relative z-20 flex flex-wrap items-center justify-center gap-2 sm:gap-3 ${liveTutorWhiteboardStarted ? 'mt-5' : ''} mb-6`}>
                  {([
                    { key: 'problems' as const, label: 'Practice', icon: Pencil, color: '#42C52D' },
                    { key: 'skills' as const, label: 'Math Skills', icon: Brain, color: '#F59E0B' },
                    { key: 'amc8' as const, label: 'AMC-8 Problems', icon: Trophy, color: '#8B8CFD' },
                    { key: 'history' as const, label: 'Social Studies', icon: BookOpen, color: '#D97706' },
                    { key: 'science' as const, label: 'Science', icon: FlaskConical, color: '#06B6D4' },
                  ]).map((tab) => {
                    const isActive = liveTutorActiveTab === tab.key;
                    const TabIcon = tab.icon;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => { stopAutoRotation(); setLiveTutorActiveTab(tab.key); }}
                        className={`relative flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold tracking-wide transition-all sm:px-6 sm:py-3 sm:text-base ${isActive
                          ? 'text-white shadow-md'
                          : 'bg-[color:var(--surface-1)] text-[color:var(--gray-500)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--gray-700)]'
                          }`}
                        style={isActive ? { backgroundColor: tab.color } : undefined}
                      >
                        <TabIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${isActive ? 'text-white' : 'text-[color:var(--gray-400)]'}`} strokeWidth={2.5} />
                        {tab.label}
                        <span
                          className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-[3px] rounded-full transition-all ${isActive ? 'w-8' : 'w-0 bg-transparent'}`}
                          style={isActive ? { backgroundColor: tab.color } : undefined}
                        />
                      </button>
                    );
                  })}
                </div>

                {/* Carousel */}
                <div
                  ref={carouselScrollRef}
                  onMouseDown={handleCarouselDragStart}
                  onMouseMove={handleCarouselDragMove}
                  onMouseUp={handleCarouselDragEnd}
                  onMouseLeave={handleCarouselDragEnd}
                  onTouchStart={handleCarouselDragStart}
                  onTouchMove={handleCarouselDragMove}
                  onTouchEnd={handleCarouselDragEnd}
                  className="relative z-20 flex items-start gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [touch-action:pan-y] [will-change:scroll-position] select-none cursor-grab active:cursor-grabbing"
                >
                  {liveTutorActiveTab === 'problems' && (
                    isLoadingProblems && liveTutorProblems.length === 0 ? (
                      <div className="text-sm text-[color:var(--gray-500)]">Loading practice problems...</div>
                    ) : liveTutorProblems.length === 0 ? (
                      <div className="text-sm text-[color:var(--gray-500)]">No practice problems available yet.</div>
                    ) : (
                      [...liveTutorProblems, ...liveTutorProblems, ...liveTutorProblems].map((problem, index) => {
                        const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                        const text = formatProblemLabel(problem.question);
                        return (
                          <button
                            key={`${problem.id}-${index}`}
                            type="button"
                            onClick={() => void handleSelectLiveTutorProblem(problem.id)}
                            className={`relative flex-shrink-0 min-w-[140px] max-w-[280px] sm:min-w-[160px] sm:max-w-[320px] flex items-center justify-center overflow-hidden rounded-2xl border px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer ${isSelected
                              ? 'border-[#42C52D]/40 bg-[#EDFDF6] text-[#2D7A1E]'
                              : 'border-[#D7D9E0] bg-[#F6FDFB] text-[#4D5059]'
                              }`}
                            style={{ height: cardHeight }}
                          >

                            <div className="pointer-events-none w-full overflow-hidden">
                              <MarkdownComponent
                                content={text}
                                textStyle={{
                                  fontSize: cardFontSize,
                                  fontWeight: 400,
                                  color: 'inherit',
                                  textAlign: 'center',
                                  lineHeight: 1.4,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              />
                            </div>
                          </button>
                        );
                      })
                    )
                  )}

                  {liveTutorActiveTab === 'skills' && (
                    isLoadingSkills ? null : liveTutorMathSkills.length === 0 ? (
                      <div className="text-sm text-[color:var(--gray-500)]">No math skills available yet.</div>
                    ) : (
                      [...liveTutorMathSkills, ...liveTutorMathSkills, ...liveTutorMathSkills].map((skill, index) => {
                        const isSelected = liveTutorSelection?.kind === 'skill' && liveTutorSelection.skillId === skill.skill_id;
                        return (
                          <button
                            key={`${skill.skill_id}-${index}`}
                            type="button"
                            onClick={() => void handleSelectLiveTutorSkill(skill)}
                            className={`relative flex-shrink-0 min-w-[140px] max-w-[280px] sm:min-w-[160px] sm:max-w-[320px] flex items-center justify-center overflow-hidden rounded-2xl border px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer ${isSelected
                              ? 'border-[#F59E0B]/40 bg-[#FFFBF0] text-[#92600A]'
                              : 'border-[#D7D9E0] bg-[#FFFDF7] text-[#4D5059]'
                              }`}
                            style={{ height: cardHeight }}
                          >

                            <div className="pointer-events-none w-full overflow-hidden">
                              <MarkdownComponent
                                content={skill.title}
                                textStyle={{
                                  fontSize: cardFontSize,
                                  fontWeight: 400,
                                  color: 'inherit',
                                  textAlign: 'center',
                                  lineHeight: 1.4,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              />
                            </div>
                          </button>
                        );
                      })
                    )
                  )}

                  {liveTutorActiveTab === 'history' && (
                    isLoadingHistoryProblems ? (
                      <div className="text-sm text-[color:var(--gray-500)]">Loading history problems...</div>
                    ) : historyProblems.length === 0 ? (
                      <div className="text-sm text-[color:var(--gray-500)]">No history problems available yet.</div>
                    ) : (
                      [...historyProblems, ...historyProblems, ...historyProblems].map((problem, index) => {
                        const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                        const text = formatProblemLabel(problem.question);
                        return (
                          <button
                            key={`${problem.id}-${index}`}
                            type="button"
                            onClick={() => void handleSelectHistoryProblem(problem.id)}
                            className={`relative flex-shrink-0 min-w-[140px] max-w-[280px] sm:min-w-[160px] sm:max-w-[320px] flex items-center justify-center overflow-hidden rounded-2xl border px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer ${isSelected
                              ? 'border-[#D97706]/40 bg-[#FFFBEB] text-[#92600A]'
                              : 'border-[#D7D9E0] bg-[#FFFDF7] text-[#4D5059]'
                              }`}
                            style={{ height: cardHeight }}
                          >
                            <div className="pointer-events-none w-full overflow-hidden">
                              <MarkdownComponent
                                content={text}
                                textStyle={{
                                  fontSize: cardFontSize,
                                  fontWeight: 400,
                                  color: 'inherit',
                                  textAlign: 'center',
                                  lineHeight: 1.4,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              />
                            </div>
                          </button>
                        );
                      })
                    )
                  )}

                  {liveTutorActiveTab === 'science' && (
                    isLoadingScienceProblems ? (
                      <div className="text-sm text-[color:var(--gray-500)]">Loading science problems...</div>
                    ) : scienceProblems.length === 0 ? (
                      <div className="text-sm text-[color:var(--gray-500)]">No science problems available yet.</div>
                    ) : (
                      [...scienceProblems, ...scienceProblems, ...scienceProblems].map((problem, index) => {
                        const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                        const text = formatProblemLabel(problem.question);
                        return (
                          <button
                            key={`${problem.id}-${index}`}
                            type="button"
                            onClick={() => void handleSelectScienceProblem(problem.id)}
                            className={`relative flex-shrink-0 min-w-[140px] max-w-[280px] sm:min-w-[160px] sm:max-w-[320px] flex items-center justify-center overflow-hidden rounded-2xl border px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer ${isSelected
                              ? 'border-[#06B6D4]/40 bg-[#ECFEFF] text-[#0E7490]'
                              : 'border-[#D7D9E0] bg-[#F0FEFF] text-[#4D5059]'
                              }`}
                            style={{ height: cardHeight }}
                          >
                            <div className="pointer-events-none w-full overflow-hidden">
                              <MarkdownComponent
                                content={text}
                                textStyle={{
                                  fontSize: cardFontSize,
                                  fontWeight: 400,
                                  color: 'inherit',
                                  textAlign: 'center',
                                  lineHeight: 1.4,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              />
                            </div>
                          </button>
                        );
                      })
                    )
                  )}

                  {liveTutorActiveTab === 'amc8' && (
                    isLoadingAmcProblems ? (
                      <div className="text-sm text-[color:var(--gray-500)]">Loading AMC 8 problems...</div>
                    ) : amcProblems.length === 0 ? (
                      <div className="text-sm text-[color:var(--gray-500)]">No AMC 8 problems available yet.</div>
                    ) : (
                      [...amcProblems, ...amcProblems, ...amcProblems].map((problem, index) => {
                        const isSelected = liveTutorSelection?.kind === 'problem' && liveTutorSelection.problem.id === problem.id;
                        const qNum = problem.id.replace('amc8-2026-q', '2026-Q');
                        const text = formatProblemLabel(problem.question);
                        return (
                          <button
                            key={`${problem.id}-${index}`}
                            type="button"
                            onClick={() => void handleSelectAmcProblem(problem.id)}
                            className={`relative flex-shrink-0 min-w-[140px] max-w-[280px] sm:min-w-[160px] sm:max-w-[320px] flex flex-col items-center justify-center overflow-hidden rounded-2xl border px-3 py-1.5 sm:px-4 sm:py-2 text-sm font-semibold transition-shadow hover:shadow-md cursor-pointer ${isSelected
                              ? 'border-[#8B8CFD]/40 bg-[#F0F0FF] text-[#5B5CC7]'
                              : 'border-[#D7D9E0] bg-[#FAFAFF] text-[#4D5059]'
                              }`}
                            style={{ height: cardHeight }}
                          >

                            <span className="absolute top-1.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider text-[#7C7DED]">{qNum}</span>
                            <div className="pointer-events-none w-full overflow-hidden mt-3">
                              <MarkdownComponent
                                content={text}
                                textStyle={{
                                  fontSize: cardFontSize,
                                  fontWeight: 400,
                                  color: 'inherit',
                                  textAlign: 'center',
                                  lineHeight: 1.4,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              />
                            </div>
                          </button>
                        );
                      })
                    )
                  )}
                </div>

                {/* "Or ask your own problem" input */}
                <div className="relative z-20 mt-6 mb-8">
                  <p className="mb-3 text-center text-sm font-bold text-[#6D7492] md:text-left" style={{ paddingLeft: 24 }}>Or upload your own problem</p>
                  <div
                    className="rounded-2xl bg-white py-1 px-4 sm:py-1 sm:px-4 border border-[#D7D9E0]"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <div className="flex-1 rounded-xl bg-white px-2 py-2">
                        <div className="flex items-center gap-2">
                          <textarea
                            value={homeUploadText}
                            onChange={(e) => setHomeUploadText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                e.preventDefault();
                                void handleHomeUploadSubmit();
                              }
                            }}
                            placeholder="Type your own math problem here..."
                            className="h-10 w-full resize-none appearance-none bg-transparent px-1 text-sm text-[#6D7492] outline-none ring-0 focus:border-transparent focus:outline-none focus:ring-0 focus:shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden leading-10"
                            style={{ outline: 'none', boxShadow: 'none', WebkitAppearance: 'none', marginLeft: -5 } as React.CSSProperties}
                          />
                        </div>
                        {homeUploadFileName && (
                          <p className="mt-1 px-1 text-xs text-[#6D7492]">Image selected: {homeUploadFileName}</p>
                        )}
                      </div>
                      <div className="relative flex-shrink-0" ref={homeUploadMenuRef}>
                        <button
                          type="button"
                          onClick={handleToggleHomeUploadMenu}
                          aria-label="Open image upload menu"
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#F6F7FF] text-[#6D7492] transition hover:bg-[#EDEDFF]"
                        >
                          <ImagePlus className="h-5 w-5" />
                        </button>
                        {isHomeUploadMenuOpen && (
                          <div className="absolute right-0 top-10 z-20 w-44 rounded-xl border border-[#D7D9E0] bg-white p-1 shadow-[0_12px_24px_rgba(0,0,0,0.08)]">
                            <button type="button" onClick={handlePickHomeImage} className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-[#6D7492] transition hover:bg-[#F6F7FF]">Upload image</button>
                            <button type="button" onClick={() => setIsHomeUploadMenuOpen(false)} className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-[#6D7492] transition hover:bg-[#F6F7FF]">Cancel</button>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleHomeUploadSubmit()}
                        disabled={(!homeUploadText.trim() && !homeUploadFile) || isHomeParseLoading}
                        className="inline-flex h-10 items-center justify-center rounded-xl px-7 text-sm font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ backgroundColor: '#8B8CFD' }}
                      >
                        {isHomeParseLoading ? (
                          <span className="inline-flex items-center gap-2">
                            Generating...
                          </span>
                        ) : (
                          'Upload'
                        )}
                      </button>
                    </div>
                  </div>
                  <input ref={homeUploadInputRef} type="file" accept="image/*" onChange={handleHomeUploadFileChange} className="hidden" />
                  {isHomeParseErrorOpen && homeParseErrorMessage && (
                    <p className="mt-2 text-center text-sm text-red-500">{homeParseErrorMessage}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 第四页 – Textbook Alignment (bordered cards) */}
          <div ref={page3Ref} className="min-h-screen w-screen flex items-center justify-center bg-white">
            <div className="w-full max-w-[1200px] px-4 sm:px-6 py-16 md:py-20">

              {/* Headline */}
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.08 }}
              >
                <h2 className="display-text-semi text-4xl font-bold leading-tight text-black">
                  Aligned with{' '}
                  <span className="display-text-bold text-5xl md:text-[56px]" style={{ color: '#42C52D' }}>150</span>{' '}
                  Top-School Textbooks
                </h2>
                <p className="display-text text-base mt-3 tracking-wide" style={{ color: '#4D5059' }}>
                  Grades 3–8 &nbsp;·&nbsp; 100% Curriculum-Aligned &nbsp;·&nbsp; Full Chapters
                </p>
              </motion.div>

              {/* Subject cards with borders */}
              <div className="mt-12 md:mt-16 mx-auto grid w-fit grid-cols-1 min-[560px]:grid-cols-[repeat(2,minmax(0,250px))] min-[1100px]:grid-cols-[repeat(4,minmax(0,250px))] place-items-center gap-6 md:gap-8">
                {([
                  { name: 'Math', covers: ['/images/covers/envision-math.png', '/images/covers/eureka-math.png', '/images/covers/reveal-math-course1.png', '/images/covers/big-ideas-2014.png', '/images/covers/saxon-math-course1.png'] },
                  { name: 'Language Arts', covers: ['/images/covers/myperspectives-2025.png', '/images/covers/elements-lang-2nd.png', '/images/covers/vocab-classical-roots.png', '/images/covers/vocab-latin-greek.png', '/images/covers/vocab-workshop.png'] },
                  { name: 'Science', covers: ['/images/covers/amplify-science.png', '/images/covers/elevate-science.png', '/images/covers/foss-next-gen.png', '/images/covers/science-closer-look-3.png', '/images/covers/earth-science.png'] },
                  { name: 'Social Studies', covers: ['/images/covers/myworld-interactive.png', '/images/covers/history-alive-ancient.png', '/images/covers/impact-social-studies.png', '/images/covers/world-history-geography.png', '/images/covers/america-history-nation.png'] },
                ] as const).map((subj, si) => (
                  <motion.div
                    key={subj.name}
                    className="relative flex w-full max-w-[250px] flex-col items-center"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.35, delay: 0.12 + si * 0.05 }}
                  >
                    {/* Subject pill label – overlaps border by half */}
                    <div className="absolute -top-[14px] left-1/2 -translate-x-1/2 z-10">
                      <div className="display-text-semi text-sm font-semibold text-white py-1.5 rounded-full whitespace-nowrap text-center w-[130px]" style={{ backgroundColor: '#8B8CFD' }}>
                        {subj.name}
                      </div>
                    </div>
                    {/* Bordered card – equal padding all sides */}
                    <div className="w-full border-2 rounded-2xl p-5 pt-8" style={{ borderColor: 'rgba(137,140,239,0.3)' }}>
                      <div className="flex flex-col gap-1.5 items-center">
                        <div className="flex flex-row gap-1.5 justify-center">
                          {subj.covers.slice(0, 2).map((src) => (
                            <img key={src} src={src} alt="" className="rounded object-cover" style={{ width: 59, height: 80, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} />
                          ))}
                        </div>
                        <div className="flex flex-row gap-1.5 justify-center">
                          {subj.covers.slice(2).map((src) => (
                            <img key={src} src={src} alt="" className="rounded object-cover" style={{ width: 59, height: 80, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Stats row */}
              <div className="flex flex-nowrap items-center justify-center gap-10 md:gap-16 mt-14 w-full">
                {([
                  { num: '4,500+', label: 'Curated Lessons' },
                  { num: '12,000+', label: 'Concepts & Skills' },
                  { num: '100K+', label: 'Practice Problems' },
                ] as const).map((stat, i) => (
                  <div key={i} className="flex flex-col items-center w-[150px]">
                    <div className="display-text text-3xl leading-none font-medium" style={{ color: '#4D5059' }}>{stat.num}</div>
                    <div className="display-text text-sm sm:text-base font-medium text-[#4D5059] text-center leading-snug mt-1 whitespace-nowrap">{stat.label}</div>
                  </div>
                ))}
              </div>
              {/* Divider */}
              <hr className="mx-auto mt-5 border-0" style={{ maxWidth: 400, height: 1, background: '#D7D9E0' }} />

              <div className="display-text text-sm sm:text-base font-medium leading-snug text-[#4D5059] text-center mt-3">Reviewed by Tutors & Educators</div>

            </div>
          </div>

          {/* 第五页 */}
          <div className="min-h-screen w-screen flex items-center justify-center">
            <div className="flex flex-col py-10 md:py-4 w-full max-w-[1200px] px-4">
              <div className="display-text-semi text-4xl text-black font-bold w-full text-center">School-Grade Learning, <span className="display-text-bold inline-block whitespace-nowrap" style={{ color: '#42C52D', fontSize: '50px' }}>Right at Home</span></div>
              <div className="display-text text-base tracking-wide w-full text-center mt-3" style={{ color: '#4D5059' }}>Structure They Need, Freedom They Want</div>
              <div className="flex flex-col w-full overflow-hidden pb-4 pt-0 h-[380px] md:h-[550px]">
                <div
                  className="flex flex-row transition-transform duration-300 ease-in-out"
                  style={{
                    width: '300%',
                    transform: `translateX(-${currentPlatformIndex * 33.333}%)`
                  }}
                >
                  <div className="flex flex-col items-center w-1/3 flex-shrink-0 px-4">
                    <div className="w-full flex flex-row items-center justify-center">
                      <Image src={'/assets/newlanding_platform_1.png'} alt="newlanding_platform_1" width={700} height={525} className="object-contain w-[500px] md:w-[700px] h-auto" loading="lazy" sizes="(max-width: 768px) 500px, 700px" optimizedWidth={1400} />
                    </div>
                    <div className="display-text text-base text-center mt-5 max-w-[80%] md:max-w-[70%] xl:max-w-[50%]">Every subject in clear sequence, aligned with school standards. No gaps. No random worksheets. Just coverage they'll enjoy.</div>
                  </div>
                  <div className="flex flex-col items-center w-1/3 flex-shrink-0 px-4">
                    <div className="w-full flex flex-row items-center justify-center">
                      <Image src={'/assets/newlanding_platform_2_2.png'} alt="newlanding_platform_2" width={700} height={525} className="object-contain w-[500px] md:w-[700px] h-auto" loading="lazy" sizes="(max-width: 768px) 500px, 700px" optimizedWidth={1400} />
                    </div>
                    <div className="display-text text-base text-center mt-5 max-w-[80%] md:max-w-[70%] xl:max-w-[50%]">Your child learns in small, fun steps—videos, quizzes, and challenges that spark curiosity. Rewards keep them coming back, no reminders needed.</div>
                  </div>
                  <div className="flex flex-col items-center w-1/3 flex-shrink-0 px-4">
                    <div className="w-full flex flex-row items-center justify-center">
                      <Image src={'/assets/newlanding_platform_3.png'} alt="newlanding_platform_3" width={700} height={525} className="object-contain w-[500px] md:w-[700px] h-auto" loading="lazy" sizes="(max-width: 768px) 500px, 700px" optimizedWidth={1400} />
                    </div>
                    <div className="display-text text-base text-center mt-5 max-w-[80%] md:max-w-[70%] xl:max-w-[50%]">Pinpoint the right starting point in minutes with a diagnostic. We auto-assign personalized lessons, track progress online, and keep you updated.</div>
                  </div>
                </div>
              </div>
              <div className="relative w-full max-w-[1060px] mx-auto px-4">
                <div ref={platformScrollContainerRef} className="flex flex-col gap-3 py-4 sm:flex-row">
                  <button className="flex w-full flex-col sm:flex-1" onClick={() => handlePlatformIndexChange(0)}>
                    <div className="w-full h-[3px]" style={{ backgroundColor: currentPlatformIndex === 0 ? '#42C52D' : '#D7D9E0' }}></div>
                    <div className="display-text-semi text-base my-3 text-center" style={{ color: currentPlatformIndex === 0 ? '#42C52D' : '#9CA3AF' }}>Aligned with Leading Curricula</div>
                    <div className="flex flex-row items-center justify-center gap-1.5 text-xs flex-nowrap" style={{ color: currentPlatformIndex === 0 ? '#42C52D' : '#9CA3AF' }}>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 0 ? '#42C52D' : '#D7D9E0' }}>Grades 3-8</div>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 0 ? '#42C52D' : '#D7D9E0' }}>All Core Subjects</div>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 0 ? '#42C52D' : '#D7D9E0' }}>No Gaps</div>
                    </div>
                  </button>
                  <button className="flex w-full flex-col sm:flex-1" onClick={() => handlePlatformIndexChange(1)}>
                    <div className="w-full h-[3px]" style={{ backgroundColor: currentPlatformIndex === 1 ? '#FBAF2C' : '#D7D9E0' }}></div>
                    <div className="display-text-semi text-base my-3 text-center" style={{ color: currentPlatformIndex === 1 ? '#FBAF2C' : '#9CA3AF' }}>Engaging for Kids</div>
                    <div className="flex flex-row items-center justify-center gap-1.5 text-xs flex-nowrap" style={{ color: currentPlatformIndex === 1 ? '#FBAF2C' : '#9CA3AF' }}>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 1 ? '#FBAF2C' : '#D7D9E0' }}>Learn</div>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 1 ? '#FBAF2C' : '#D7D9E0' }}>Practice</div>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 1 ? '#FBAF2C' : '#D7D9E0' }}>Challenge</div>
                    </div>
                  </button>
                  <button className="flex w-full flex-col sm:flex-1" onClick={() => handlePlatformIndexChange(2)}>
                    <div className="w-full h-[3px]" style={{ backgroundColor: currentPlatformIndex === 2 ? '#8B8CFD' : '#D7D9E0' }}></div>
                    <div className="display-text-semi text-base my-3 text-center" style={{ color: currentPlatformIndex === 2 ? '#8B8CFD' : '#9CA3AF' }}>Progress Assured</div>
                    <div className="flex flex-row items-center justify-center gap-1.5 text-xs flex-nowrap" style={{ color: currentPlatformIndex === 2 ? '#8B8CFD' : '#9CA3AF' }}>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 2 ? '#8B8CFD' : '#D7D9E0' }}>One Click</div>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 2 ? '#8B8CFD' : '#D7D9E0' }}>Assign Tasks</div>
                      <div className="rounded-xl border px-1.5 py-0.5 whitespace-nowrap" style={{ borderColor: currentPlatformIndex === 2 ? '#8B8CFD' : '#D7D9E0' }}>Personalized Plan</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 第六页 */}
          <div ref={page5Ref} className="min-h-screen w-screen flex items-center justify-center" style={{ backgroundColor: interpolateColor(page5Opacity) }}>
            <div className="relative w-full max-w-[1280px] min-h-[920px] px-3 min-[1000px]:min-h-0 min-[1000px]:h-[700px] min-[1000px]:px-10 lg:h-[780px]">
              <div className="relative flex h-full flex-col items-center min-[1000px]:hidden">
                <div className="relative z-10 flex w-full flex-col items-center pt-8 text-center">
                  <div className="mb-4 max-w-[88vw]">
                    <div className="display-text-semi text-[30px] leading-[1] tracking-[-0.02em]" style={{ color: '#2C5FB2' }}>
                      Learn, Play,
                    </div>
                    <div className="display-text-semi mt-1 text-[30px] leading-[1] tracking-[-0.02em]" style={{ color: '#2C5FB2' }}>
                      Achieve Together
                    </div>
                    <div className="display-text text-base tracking-wide mt-3" style={{ color: '#4D5059' }}>
                      Kids stay motivated when learning feels like play
                    </div>
                  </div>

                  <div className="relative mt-[-12px] flex h-[250px] w-[250px] items-center justify-center">
                    <Image
                      src="/assets/learn-play-main-bg.png"
                      alt="Learn and play main background"
                      width={250}
                      height={250}
                      className="absolute object-contain w-full h-auto"
                      loading="lazy"
                      sizes="250px"
                    />
                    <Image
                      src="/assets/learn-play-main.png"
                      alt="Learn and play main"
                      width={110}
                      height={110}
                      className="relative z-10 mt-[-10px] h-[110px] w-[110px] object-contain"
                      loading="lazy"
                      sizes="110px"
                    />
                  </div>
                </div>

                <div className="relative z-20 mt-4 grid w-full max-w-[560px] grid-cols-2 items-stretch justify-items-center gap-x-2 gap-y-3 pb-8 min-[420px]:gap-x-3 min-[700px]:gap-x-4 min-[700px]:gap-y-4">
                  <div className="flex h-[252px] min-[420px]:h-[270px] w-full max-w-[248px] flex-col rounded-2xl bg-white p-2.5 min-[420px]:p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                    <div className="flex h-[110px] min-[420px]:h-[132px] min-[700px]:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                      <Image
                        src="/assets/learn-play-1.png"
                        alt="MiniPet"
                        width={180}
                        height={180}
                        className="object-contain w-full h-full scale-110"
                        loading="lazy"
                        sizes="248px"
                      />
                    </div>
                    <div className="mt-1.5 text-left">
                      <div className="display-text-semi text-lg leading-6 text-[#1F2A44]">MiniPet</div>
                      <div className="display-text mt-1.5 text-sm leading-5 text-[#9CA3AF]">
                        Raise and care for your learning companion
                      </div>
                    </div>
                    <div className="mt-auto flex justify-end pt-2">
                      <div className="rounded-full bg-[#42C52D] px-3.5 py-1.5 text-center">
                        <div className="display-text-semi text-sm text-white">Adopt Pet</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex h-[252px] min-[420px]:h-[270px] w-full max-w-[248px] flex-col rounded-2xl bg-white p-2.5 min-[420px]:p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                    <div className="flex h-[110px] min-[420px]:h-[132px] min-[700px]:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                      <Image
                        src="/assets/learn-play-2.png"
                        alt="World Landmarks Progress"
                        width={180}
                        height={180}
                        className="object-contain w-full h-full scale-110"
                        loading="lazy"
                        sizes="248px"
                      />
                    </div>
                    <div className="mt-1.5">
                      <div className="display-text-semi text-lg leading-6 text-[#1F2A44]">World Landmarks Progress</div>
                      <div className="mt-2 flex items-center gap-2.5">
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#E6EEFB]">
                          <div className="h-full w-[95%] rounded-full bg-gradient-to-r from-[#2C5FB2] to-[#7A6AF6]"></div>
                        </div>
                        <div className="display-text-semi text-sm text-[#2C5FB2]">95%</div>
                      </div>
                    </div>
                    <div className="mt-auto flex justify-end pt-2">
                      <div className="rounded-full bg-[#8B8CFD] px-3.5 py-1.5 text-center">
                        <div className="display-text-semi text-sm text-white">Woohoo!</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex h-[252px] min-[420px]:h-[270px] w-full max-w-[248px] flex-col rounded-2xl bg-white p-2.5 min-[420px]:p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                    <div className="flex h-[110px] min-[420px]:h-[132px] min-[700px]:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                      <Image
                        src="https://syllabus-cdn.pengi.ai/assets/learn-play-3.png?v=2"
                        alt="Drawing Fish"
                        width={248}
                        height={148}
                        className="object-cover w-full h-full"
                        loading="lazy"
                        sizes="248px"
                      />
                    </div>
                    <div className="mt-1.5">
                      <div className="display-text-semi text-lg leading-6 text-[#1F2A44]">Drawing Fish</div>
                      <div className="display-text mt-1.5 text-sm leading-5 text-[#9CA3AF]">
                        Express creativity througth art and imagination
                      </div>
                    </div>
                    <div className="mt-auto flex justify-end pt-2">
                      <div className="rounded-full bg-[#42C52D] px-3.5 py-1.5 text-center">
                        <div className="display-text-semi text-sm text-white">Start Drawing</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex h-[252px] min-[420px]:h-[270px] w-full max-w-[248px] flex-col rounded-2xl bg-white p-2.5 min-[420px]:p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                    <div className="flex h-[110px] min-[420px]:h-[132px] min-[700px]:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                      <Image
                        src="/assets/learn-play-4.png"
                        alt="January Winter Sparks Event"
                        width={248}
                        height={148}
                        className="object-cover w-full h-full"
                        loading="lazy"
                        sizes="248px"
                      />
                    </div>
                    <div className="mt-1.5">
                      <div className="display-text-semi text-lg leading-6 text-[#1F2A44]">
                        January Winter Sparks Event
                      </div>
                    </div>
                    <div className="mt-auto flex justify-end pt-2">
                      <div className="rounded-full bg-[#8B8CFD] px-3.5 py-1.5 text-center">
                        <div className="display-text-semi text-sm text-white">Join Now</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative hidden h-full min-[1000px]:block">
                <div className="pointer-events-none absolute inset-0 z-0">
                  <div
                    className="absolute h-[220px] w-[460px] opacity-85 blur-[20px]"
                    style={{
                      left: '50%',
                      top: 'calc(50% - 110px)',
                      transform: 'rotate(-148deg)',
                      transformOrigin: '0 50%',
                      background: 'linear-gradient(90deg, #D7EEFC 0%, #B8E0F8 52%, #9CCDEA 100%)',
                      clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
                    }}
                  />
                  <div
                    className="absolute h-[220px] w-[460px] opacity-85 blur-[20px]"
                    style={{
                      left: '50%',
                      top: 'calc(50% - 110px)',
                      transform: 'rotate(-32deg)',
                      transformOrigin: '0 50%',
                      background: 'linear-gradient(90deg, #D7EEFC 0%, #B8E0F8 52%, #9CCDEA 100%)',
                      clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
                    }}
                  />
                  <div
                    className="absolute h-[220px] w-[460px] opacity-85 blur-[20px]"
                    style={{
                      left: '50%',
                      top: 'calc(50% - 110px)',
                      transform: 'rotate(148deg)',
                      transformOrigin: '0 50%',
                      background: 'linear-gradient(90deg, #D7EEFC 0%, #B8E0F8 52%, #9CCDEA 100%)',
                      clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
                    }}
                  />
                  <div
                    className="absolute h-[220px] w-[460px] opacity-85 blur-[20px]"
                    style={{
                      left: '50%',
                      top: 'calc(50% - 110px)',
                      transform: 'rotate(32deg)',
                      transformOrigin: '0 50%',
                      background: 'linear-gradient(90deg, #D7EEFC 0%, #B8E0F8 52%, #9CCDEA 100%)',
                      clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
                    }}
                  />
                </div>

                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="mb-5 sm:mb-6 max-w-[35vw]">
                      <div className="display-text-semi text-3xl sm:text-4xl" style={{ color: '#2C5FB2' }}>
                        Learn, Play,
                      </div>
                      <div className="display-text-semi text-3xl sm:text-4xl" style={{ color: '#2C5FB2' }}>
                        Achieve Together
                      </div>
                      <div className="display-text text-base tracking-wide mt-3" style={{ color: '#4D5059' }}>
                        Kids stay motivated when learning feels like play
                      </div>
                    </div>
                    <div className="relative mt-[-100px] flex h-[500px] w-[500px] items-center justify-center">
                      <Image
                        src="/assets/learn-play-main-bg.png"
                        alt="Learn and play main background"
                        width={500}
                        height={500}
                        className="absolute object-contain w-full h-auto"
                        loading="lazy"
                        sizes="500px"
                      />
                      <Image
                        src="/assets/learn-play-main.png"
                        alt="Learn and play main"
                        width={200}
                        height={200}
                        className="relative z-10 mt-[-30px] h-[200px] w-[200px] object-contain"
                        loading="lazy"
                        sizes="200px"
                      />
                    </div>
                  </div>
                </div>

                <div className="absolute left-12 top-16 z-20 -translate-y-[10px] sm:left-10 sm:top-14 lg:left-20 flex h-[252px] sm:h-[270px] w-[232px] sm:w-[248px] rotate-[8deg] flex-col rounded-2xl bg-white p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                  <div className="flex h-[132px] sm:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                    <Image
                      src="/assets/learn-play-1.png"
                      alt="MiniPet"
                      width={180}
                      height={180}
                      className="object-contain w-full h-full scale-110"
                      loading="lazy"
                      sizes="260px"
                    />
                  </div>
                  <div className="mt-2 text-left">
                    <div className="display-text-semi text-lg sm:text-xl text-[#1F2A44]">MiniPet</div>
                    <div className="display-text mt-1.5 text-sm leading-5 text-[#9CA3AF]">
                      Raise and care for your learning companion
                    </div>
                  </div>
                  <div className="mt-auto flex justify-end pt-2.5">
                    <div className="rounded-full bg-[#42C52D] px-3.5 py-1.5 text-center">
                      <div className="display-text-semi text-sm text-white">Adopt Pet</div>
                    </div>
                  </div>
                </div>

                <div className="absolute right-12 top-16 z-20 -translate-y-[10px] sm:right-10 sm:top-14 lg:right-20 flex h-[252px] sm:h-[270px] w-[232px] sm:w-[248px] rotate-[-8deg] flex-col rounded-2xl bg-white p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                  <div className="flex h-[132px] sm:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                    <Image
                      src="/assets/learn-play-2.png"
                      alt="World Landmarks Progress"
                      width={180}
                      height={180}
                      className="object-contain w-full h-full scale-110"
                      loading="lazy"
                      sizes="260px"
                    />
                  </div>
                  <div className="mt-2">
                    <div className="display-text-semi text-lg sm:text-xl leading-6 text-[#1F2A44]">World Landmarks Progress</div>
                    <div className="mt-2 flex items-center gap-2.5">
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#E6EEFB]">
                        <div className="h-full w-[95%] rounded-full bg-gradient-to-r from-[#2C5FB2] to-[#7A6AF6]"></div>
                      </div>
                      <div className="display-text-semi text-sm text-[#2C5FB2]">95%</div>
                    </div>
                  </div>
                  <div className="mt-auto flex justify-end pt-2.5">
                    <div className="rounded-full px-3.5 py-1.5 text-center" style={{ backgroundColor: '#8B8CFD' }}>
                      <div className="display-text-semi text-sm text-white">Woohoo!</div>
                    </div>
                  </div>
                </div>

                <div className="absolute left-12 bottom-16 z-20 translate-y-[10px] sm:left-10 sm:bottom-14 lg:left-20 flex h-[252px] sm:h-[270px] w-[232px] sm:w-[248px] rotate-[-8deg] flex-col rounded-2xl bg-white p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                  <div className="flex h-[132px] sm:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                    <Image
                      src="https://syllabus-cdn.pengi.ai/assets/learn-play-3.png?v=2"
                      alt="Drawing Fish"
                      width={248}
                      height={148}
                      className="object-cover w-full h-full"
                      loading="lazy"
                      sizes="260px"
                    />
                  </div>
                  <div className="mt-2">
                    <div className="display-text-semi text-lg sm:text-xl text-[#1F2A44]">Drawing Fish</div>
                    <div className="display-text mt-1.5 text-sm leading-5 text-[#9CA3AF]">
                      Express creativity througth art and imagination
                    </div>
                  </div>
                  <div className="mt-auto flex justify-end pt-2.5">
                    <div className="rounded-full bg-[#42C52D] px-3.5 py-1.5 text-center">
                      <div className="display-text-semi text-sm text-white">Start Drawing</div>
                    </div>
                  </div>
                </div>

                <div className="absolute right-12 bottom-16 z-20 translate-y-[10px] sm:right-10 sm:bottom-14 lg:right-20 flex h-[252px] sm:h-[270px] w-[232px] sm:w-[248px] rotate-[8deg] flex-col rounded-2xl bg-white p-3 shadow-[0_18px_40px_rgba(156,205,234,0.24)]">
                  <div className="flex h-[132px] sm:h-[148px] items-center justify-center overflow-hidden rounded-[16px] bg-[#F6FAFF]">
                    <Image
                      src="/assets/learn-play-4.png"
                      alt="January Winter Sparks Event"
                      width={248}
                      height={148}
                      className="object-cover w-full h-full"
                      loading="lazy"
                      sizes="260px"
                    />
                  </div>
                  <div className="mt-2">
                    <div className="display-text-semi text-lg sm:text-xl leading-6 text-[#1F2A44]">
                      January Winter Sparks Event
                    </div>
                  </div>
                  <div className="mt-auto flex justify-end pt-2.5">
                    <div className="rounded-full px-3.5 py-1.5 text-center" style={{ backgroundColor: '#8B8CFD' }}>
                      <div className="display-text-semi text-sm text-white">Join Now</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 第七页 */}
          <div className="min-h-screen w-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F7FF 40%, #F8F7FF 60%, #FFFFFF 100%)' }}>
            <div className="relative flex flex-col items-center justify-center w-full max-w-[1062px] px-4">
              <div className="display-text-semi text-4xl font-bold w-full text-center mt-10" style={{ color: '#1F2A44' }}>
                Real Voices,<br className="block sm:hidden" /> <span className="text-[#42C52D]">Real Results</span>
              </div>
              <div className="w-full flex flex-col items-center py-10">
                <div className="w-full flex flex-col lg:flex-row gap-5">
                  {/* Featured card (Katie) */}
                  <div
                    className="lg:w-[420px] flex-shrink-0 bg-[#8B8CFD] rounded-2xl p-8 flex flex-col justify-between text-white"
                    style={{ boxShadow: '0 4px 20px rgba(139,140,253,0.25), 0 2px 16px rgba(0,0,0,0.08)' }}
                  >
                    <div className="flex gap-1">
                      {new Array(5).fill(0).map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-white" fill="white" />
                      ))}
                    </div>
                    <div className="text-lg leading-[1.8] opacity-95">
                      &ldquo;<span className="font-light">What is this Pengi thing? It&apos;s like rlly cool, I get to learn everything from my school beforehand —</span> <span className="display-text-bold">THIS IS A MIRACLE. IT HAS THE EXACT BOOKS TOO, MY LIFE HAS BECOME TOO GOOD TO BE TRUE!</span> <span className="font-light">I use it like every day now, even on weekends. It doesn&apos;t even feel like studying tbh.</span>&rdquo;
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="rounded-full border-2 border-white/60">
                        <Image src={redesignCFeatured.avatar} alt={redesignCFeatured.name} width={52} height={52} className="object-contain rounded-full" optimizedWidth={104} />
                      </div>
                      <div>
                        <div className="display-text-semi text-base">{redesignCFeatured.name}</div>
                        <div className="display-text text-sm opacity-80">{redesignCFeatured.content}</div>
                      </div>
                    </div>
                  </div>

                  {/* Grid of remaining cards: col 1 = Anika + Avery, col 2 = Ms. Ramirez + Richard */}
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Column 1: Anika (long text, flex-[3]) + Avery (short text, flex-[2]) */}
                    <div className="flex flex-col gap-4">
                      {[redesignCGrid[0], redesignCGrid[3]].map((item, index) => (
                        <div
                          key={index}
                          className={`bg-white rounded-xl p-5 flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${index === 0 ? 'flex-[3]' : 'flex-[2]'}`}
                          style={{ boxShadow: '0 4px 16px rgba(139,140,253,0.10), 0 2px 8px rgba(0,0,0,0.04)' }}
                        >
                          <div>
                            <div className="flex gap-0.5 mb-2">
                              {new Array(5).fill(0).map((_, i) => (
                                <Star key={i} className="w-3 h-3 text-[#FAD97F]" fill="#FAD97F" />
                              ))}
                            </div>
                            <div className="display-text text-sm text-[#4D5059] leading-relaxed">
                              &ldquo;{item.message.trim()}&rdquo;
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 mt-4 pt-3">
                            <div className="rounded-full border-2 border-[#8B8CFD]/40">
                              <Image src={item.avatar} alt={item.name} width={36} height={36} className="object-contain rounded-full" optimizedWidth={72} />
                            </div>
                            <div>
                              <div className="display-text-semi text-sm text-[#1F2A44]">{item.name}</div>
                              <div className="display-text text-xs text-[#9CA3AF]">{item.content}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Column 2: Ms. Ramirez + Richard (equal height) */}
                    <div className="flex flex-col gap-4">
                      {[redesignCGrid[1], redesignCGrid[2]].map((item, index) => (
                        <div
                          key={index}
                          className="bg-white rounded-xl p-5 flex-1 flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                          style={{ boxShadow: '0 4px 16px rgba(139,140,253,0.10), 0 2px 8px rgba(0,0,0,0.04)' }}
                        >
                          <div>
                            <div className="flex gap-0.5 mb-2">
                              {new Array(5).fill(0).map((_, i) => (
                                <Star key={i} className="w-3 h-3 text-[#FAD97F]" fill="#FAD97F" />
                              ))}
                            </div>
                            <div className="display-text text-sm text-[#4D5059] leading-relaxed">
                              &ldquo;{item.message.trim()}&rdquo;
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 mt-4 pt-3">
                            <div className="rounded-full border-2 border-[#8B8CFD]/40">
                              <Image src={item.avatar} alt={item.name} width={36} height={36} className="object-contain rounded-full" optimizedWidth={72} />
                            </div>
                            <div>
                              <div className="display-text-semi text-sm text-[#1F2A44]">{item.name}</div>
                              <div className="display-text text-xs text-[#9CA3AF]">{item.content}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 第八页 */}
          <div ref={page7Ref} className="min-h-screen w-screen flex items-center justify-center" style={{ backgroundColor: interpolateColor(page7Opacity) }}>
            <div className="relative flex flex-col sm:flex-row w-full max-w-[1440px] px-4" style={{ height: landing_height }}>
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="flex flex-row items-center">
                  <Image className="z-10" src="/assets/landing-logo.png" alt="penguin" width={50} height={50} priority optimizedWidth={100} />
                  <div className="emphasize-text ml-5 font-bold text-3xl text-[#1F2A44] rounded-full flex items-center justify-center">
                    Meet Pengi
                  </div>
                </div>
                <div className="mt-10 flex flex-row flex-wrap gap-2 justify-center">
                  <span className="display-text-bold text-xl sm:text-3xl" style={{ color: '#42C52D' }}>Aligned.</span>
                  <span className="display-text-bold text-xl sm:text-3xl" style={{ color: '#FBAF2C' }}>Engaging.</span>
                  <span className="display-text-bold text-xl sm:text-3xl" style={{ color: '#8B8CFD' }}>Assured.</span>
                </div>
                <div className="display-text text-3xl text-[#4D5059] text-center mt-2">
                  Your child's best school year starts here.
                </div>
                <div className="flex flex-row items-center gap-3 mt-10">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleGetFreeAccess();
                        setLoginModalVisible(true);
                      }
                    }}
                    className="px-4 py-3 rounded-full border border-[#D7D9E0] focus:outline-none focus:ring-2 focus:ring-[#42C52D] focus:border-transparent text-base w-[200px] sm:w-[350px] bg-white"
                  />
                  <div className="-ml-15 mt-6">
                    <NormalButton
                      onClick={() => {
                        handleGetFreeAccess();
                        setLoginModalVisible(true);
                      }}
                      className="z-10 w-[160px] sm:w-[200px] lg:w-[230px]"
                    >
                      <span className="text-base sm:text-lg lg:text-xl py-[5px]">Get Free Access</span>
                    </NormalButton>
                    <div className="display-text text-sm font-bold mt-1 text-center" style={{ color: '#735648' }}>No credit card needed</div>
                  </div>
                </div>
              </div>
              <div className="absolute hidden lg:block flex-1 items-center justify-center">
                <Image src="/assets/newlanding_footprint.png" alt="newlanding_footprint" width={500} height={500} className="object-contain" optimizedWidth={500} />
              </div>
              <div className="absolute hidden lg:block" style={{ top: '120px', right: '250px' }}>
                <Image src="/assets/landing-sun.png" alt="landing-sun" width={116} height={116} optimizedWidth={232} />
              </div>
              <div className="absolute hidden lg:block" style={{ bottom: '50px', left: '100px' }}>
                <Image src="/assets/landing-star.png" alt="landing-star" width={110} height={100} optimizedWidth={220} />
              </div>
            </div>
          </div>

          <footer className="w-full border-t border-[#D7D9E0] bg-[linear-gradient(180deg,#ffffff_0%,#f7fbff_100%)]">
            <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-12 px-6 py-14 lg:px-10">
              <div className="flex flex-col gap-10 lg:flex-row lg:justify-between">
                <div className="max-w-[420px]">
                  <div className="flex items-center gap-4">
                    <Image src="/assets/landing-logo.png" alt="Pengi" width={48} height={48} optimizedWidth={96} />
                    <div className="emphasize-text text-3xl font-bold text-[#1F2A44]">Pengi</div>
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    {[
                      { label: 'YouTube', href: 'https://www.youtube.com/@Pengi_ai', icon: FaYoutube },
                      { label: 'Instagram', href: 'https://www.instagram.com/pengi.ai/', icon: FaInstagram },
                      { label: 'TikTok', href: 'https://www.tiktok.com/@pengi_yourcutestudybuddy', icon: FaTiktok },
                    ].map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={link.label}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#D7D9E0] bg-white text-[#4D5059] transition hover:border-[#42C52D] hover:text-[#42C52D]"
                      >
                        <link.icon className="h-5 w-5" />
                      </a>
                    ))}
                  </div>
                </div>

                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {footerGroups.map((group) => (
                    <div key={group.title} className="min-w-[170px]">
                      <h3 className="display-text-semi text-sm uppercase tracking-[0.18em] text-[#8A94AB]">
                        {group.title}
                      </h3>
                      <div className="mt-4 flex flex-col gap-3">
                        {group.links.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            className="display-text text-base text-[#1F2A44] transition-colors hover:text-[#42C52D]"
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-4 border-t border-[#E3E8F2] pt-6 text-sm text-[#6B7280] md:flex-row md:items-center md:justify-between">
                <div className="display-text">
                  © 2026 Pengi. Built for students, parents, and educators.
                </div>
                <div className="display-text flex items-center gap-4 self-end text-sm text-[#6B7280]">
                  <Link href="/privacy-policy" className="transition-colors hover:text-[#42C52D]">
                    Privacy Policy
                  </Link>
                  <Link href="/terms" className="transition-colors hover:text-[#42C52D]">
                    Terms of Use
                  </Link>
                </div>
              </div>
            </div>
          </footer>
        </>
      )}

      <Suspense fallback={null}>
        <LoginModal
          visible={loginModalVisible}
          onClose={() => setLoginModalVisible(false)}
          email={loginEmail}
          inviteCode={inviteCode || undefined}
          isFromShare={isFromShare}
        />
      </Suspense>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}
